import { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { RefreshCw, Tv, Copy, Maximize, Eye, EyeOff, RotateCw, ChevronDown, FileText, Trophy, User, X, XCircle, Star, MessageSquare, Monitor, Play, Image, Sun, Film, Upload, Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { Sidebar, OddsTable, BongModal, NavnsuperModal, CrawlModal } from "../components/controlpanel";
import { PTZControl } from "../components/PTZControl";
import { AutoTracker } from "../components/AutoTracker";

// Always use relative URLs - works on both preview and deployed domains
const API = '/api';

export default function ControlPanel() {
  const [availableGames, setAvailableGames] = useState([]);
  const [selectedGame, setSelectedGame] = useState(null);
  const [selectedLeg, setSelectedLeg] = useState(1);
  const [gameData, setGameData] = useState(null);
  const [raceData, setRaceData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [connected, setConnected] = useState(null); // null = checking, true = connected, false = disconnected
  const [overlayHealth, setOverlayHealth] = useState({ status: 'checking', clients: 0, seconds: null });
  const [lastUpdate, setLastUpdate] = useState(null);
  const [gamesLastUpdate, setGamesLastUpdate] = useState(null);

  // Display mode state (moved to top for callbacks)
  const [displayMode, setDisplayMode] = useState('overlay');
  const [fullscreenContent, setFullscreenContent] = useState('odds'); // 'odds' or 'bong'

  // Bong state
  const [showBongModal, setShowBongModal] = useState(false);
  const [bongData, setBongData] = useState({});
  const [bongDataGuest, setBongDataGuest] = useState({});
  const [bongModalTab, setBongModalTab] = useState('tips'); // 'tips' or 'guest'
  const [bongNameTips, setBongNameTips] = useState('Tipsekspert');
  const [bongNameGuest, setBongNameGuest] = useState('Gjest');
  const [showDualBong, setShowDualBong] = useState(false);
  const [showBongOnOverlay, setShowBongOnOverlay] = useState(false);
  const [bongSelectedLegs, setBongSelectedLegs] = useState([]); // Which legs to show on overlay (empty = all)
  const [showOddsOnOverlay, setShowOddsOnOverlay] = useState(true);
  const [loopActive, setLoopActive] = useState(false);
  const loopIntervalRef = useRef(null);
  // Refs so the loop's setInterval callback always reads the *latest*
  // selectedGame / gameData — otherwise switching game type (V65 → V75 → DD)
  // mid-loop would keep looping the old game due to stale closure.
  const loopSelectedGameRef = useRef(null);
  const loopGameDataRef = useRef(null);
  const loopLegIndexRef = useRef(0);

  // Super/Lower Third state
  const [superActive, setSuperActive] = useState(false);
  const [selectedHorseForSuper, setSelectedHorseForSuper] = useState(null);

  // Banker alert state
  const [bankerActive, setBankerActive] = useState(false);

  // Message popup state
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [messageText, setMessageText] = useState('');
  const [messageActive, setMessageActive] = useState(false);

  // V75 popup state
  const [showV75Modal, setShowV75Modal] = useState(false);
  const [v75Text, setV75Text] = useState('');
  const [v75Active, setV75Active] = useState(false);

  // Results overlay state
  const [resultsActive, setResultsActive] = useState(false);
  const [resultsLoading, setResultsLoading] = useState(false);
  const [showResultsModal, setShowResultsModal] = useState(false);
  const [resultsBong, setResultsBong] = useState(() => {
    try {
      const stored = localStorage.getItem('atg-results-bong');
      return stored ? JSON.parse(stored) : {};
    } catch { return {}; }
  });

  // Weather state
  const [showWeather, setShowWeather] = useState(false);
  const [weatherData, setWeatherData] = useState(null);
  const [loadingWeather, setLoadingWeather] = useState(false);
  const [weatherDate, setWeatherDate] = useState(() => new Date().toISOString().split('T')[0]);

  // Navnsuper state
  const [showNavnsuperModal, setShowNavnsuperModal] = useState(false);
  const [navnsuperList, setNavnsuperList] = useState([]);
  const [selectedNavnsuper, setSelectedNavnsuper] = useState(null);
  const [navnsuperName, setNavnsuperName] = useState('');
  const [navnsuperTitle, setNavnsuperTitle] = useState('');
  const [showNavnsuper, setShowNavnsuper] = useState(false);
  const [loadingNavnsupers, setLoadingNavnsupers] = useState(false);

  // Webcam background state
  const [useWebcam, setUseWebcam] = useState(false);
  const [webcamDevices, setWebcamDevices] = useState([]);

  // Loop-video state (fullscreen looping video with audio)
  const [showVideo, setShowVideo] = useState(false);
  const [videoInfo, setVideoInfo] = useState(null); // { exists, filename, version }
  const [videoUploading, setVideoUploading] = useState(false);
  const [videoProgress, setVideoProgress] = useState(0);
  const videoFileInputRef = useRef(null);

  // Wipe transition on/off (persisted). Controls whether fullscreen odds/bong use the gold wipe.
  const [useWipe, setUseWipe] = useState(() => {
    const s = typeof localStorage !== 'undefined' ? localStorage.getItem('atg-use-wipe') : null;
    return s === null ? true : s === 'true';
  });
  const toggleUseWipe = () => {
    setUseWipe((v) => {
      const nv = !v;
      try { localStorage.setItem('atg-use-wipe', String(nv)); } catch (e) { /* ignore */ }
      toast.success(nv ? 'Wipe-overgang på' : 'Wipe-overgang av', { duration: 1500 });
      return nv;
    });
  };

  // Right panel tab
  const [rightTab, setRightTab] = useState('ptz');
  const [autoTrackActive, setAutoTrackActive] = useState(false);
  const [videoControlUrl, setVideoControlUrl] = useState(() => localStorage.getItem('video-control-url') || '');
  const [videoControlUrlActive, setVideoControlUrlActive] = useState(() => localStorage.getItem('video-control-url') || '');
  const [selectedWebcamId, setSelectedWebcamId] = useState('');
  const [audioDevices, setAudioDevices] = useState([]);
  const [selectedAudioId, setSelectedAudioId] = useState('');

  // Crawl (scrolling text) state
  const [showCrawlModal, setShowCrawlModal] = useState(false);
  const [crawlText, setCrawlText] = useState('');
  const [showCrawl, setShowCrawl] = useState(false);
  const [crawlSource, setCrawlSource] = useState('manual'); // 'manual' or 'rss'
  const [rssUrl, setRssUrl] = useState('');
  const [rssHeadlines, setRssHeadlines] = useState([]);
  const [isLoadingRss, setIsLoadingRss] = useState(false);

  // Persisted bong data (from "Lag Bong" modal)
  const [persistedBongData, setPersistedBongData] = useState(() => {
    const stored = localStorage.getItem('atg-bong-data');
    if (stored) { try { return JSON.parse(stored); } catch (e) {} }
    return null;
  });
  const [persistedBongDataGuest, setPersistedBongDataGuest] = useState(null);

  // Loading state for Rikstoto data
  const [rikstotoLoading, setRikstotoLoading] = useState(false);
  const [sorlandetDays, setSorlandetDays] = useState([]);
  const [selectedDayKey, setSelectedDayKey] = useState('');

  // Ref to track if initial state has been loaded from backend
  const initialStateLoaded = useRef(false);

  // Persistent WebSocket used to push state deltas to overlay clients with
  // near-zero latency (no HTTP round-trip). Falls back to axios.post if the
  // socket isn't in OPEN state (e.g. reconnecting).
  const wsRef = useRef(null);
  const [wsReady, setWsReady] = useState(false);
  // Once the WS has failed to establish repeatedly (typically because the
  // production Emergent ingress isn't upgrading /api/ws/* to WebSocket) we
  // stop retrying and permanently use the HTTP fallback for this session.
  // This kills the wasted reconnect chatter and prevents any perception of
  // "waiting for WS" during a broadcast.
  const wsPermanentlyOffRef = useRef(false);
  
  // Preview webcam refs
  const previewVideoRef = useRef(null);
  const previewStreamRef = useRef(null);
  const [previewActive, setPreviewActive] = useState(false);

  // Fetch available webcam devices
  useEffect(() => {
    const getDevices = async () => {
      try {
        // Request permission first to get device labels
        await navigator.mediaDevices.getUserMedia({ video: true, audio: true })
          .then(stream => {
            stream.getTracks().forEach(track => track.stop());
          })
          .catch(() => {});
        
        const devices = await navigator.mediaDevices.enumerateDevices();
        const videoDevices = devices.filter(device => device.kind === 'videoinput');
        const audioInputDevices = devices.filter(device => device.kind === 'audioinput');
        setWebcamDevices(videoDevices);
        setAudioDevices(audioInputDevices);
        if (videoDevices.length > 0 && !selectedWebcamId) {
          setSelectedWebcamId(videoDevices[0].deviceId);
        }
        if (audioInputDevices.length > 0 && !selectedAudioId) {
          setSelectedAudioId(audioInputDevices[0].deviceId);
        }
      } catch (err) {
        console.error('Error getting webcam devices:', err);
      }
    };
    getDevices();
  }, []);

  // Health check - verify backend connectivity (independent of data)
  useEffect(() => {
    let mounted = true;
    let healthInterval = null;
    const checkHealth = async () => {
      try {
        await axios.get(`${API}/health`, { timeout: 5000 });
        if (mounted) setConnected(true);
      } catch {
        if (mounted) setConnected(false);
      }
    };
    checkHealth();
    healthInterval = setInterval(checkHealth, 15000);
    return () => { mounted = false; clearInterval(healthInterval); };
  }, []);

  // Overlay connection health — polls /overlay-health every 5s so the operator
  // sees at a glance whether the /overlay page is actually receiving updates.
  // Status: 'ok' green (client connected + recent activity),
  //         'stale' orange (connected but no message in >30s),
  //         'down' red (no clients for >6s — grace period prevents red flicker
  //                    during a normal WS reconnect which briefly hits 0).
  useEffect(() => {
    let mounted = true;
    let lastSeenClientsAtRef = Date.now();
    const check = async () => {
      try {
        const res = await axios.get(`${API}/overlay-health`, { timeout: 5000 });
        if (!mounted) return;
        const { clients, seconds_since_last_message } = res.data || {};
        const clientCount = clients || 0;
        if (clientCount > 0) lastSeenClientsAtRef = Date.now();
        const secondsWithoutClients = (Date.now() - lastSeenClientsAtRef) / 1000;
        let status = 'ok';
        if (clientCount === 0 && secondsWithoutClients > 6) {
          status = 'down';
        } else if (clientCount > 0) {
          status = (seconds_since_last_message == null || seconds_since_last_message <= 30) ? 'ok' : 'stale';
        } else {
          // 0 clients but within grace period — hold last-known 'ok' visual
          status = 'ok';
        }
        setOverlayHealth({ status, clients: clientCount, seconds: seconds_since_last_message });
      } catch {
        if (mounted) setOverlayHealth({ status: 'down', clients: 0, seconds: null });
      }
    };
    check();
    const t = setInterval(check, 5000);
    return () => { mounted = false; clearInterval(t); };
  }, []);

  // Control-panel WebSocket: reuses the /api/ws/overlay-state endpoint the
  // overlay uses. When the operator toggles graphics on/off we push a JSON
  // delta directly on this socket instead of doing an HTTP POST. The backend
  // then broadcasts to all overlay clients in the same event loop tick, so
  // graphics appear/disappear on the /overlay screen within ~1 WS round-trip
  // (typically 30-80ms) instead of the previous HTTP round-trip (200-500ms).
  useEffect(() => {
    let ws = null;
    let mounted = true;
    let reconnectAttempts = 0;
    let heartbeatTimer = null;
    let reconnectTimer = null;
    let lastMessageTime = Date.now();

    const connect = () => {
      if (!mounted) return;
      // Derive WS URL from the same env var HTTP uses (REACT_APP_BACKEND_URL)
      // so preview and production hit the correct backend proxy path.
      // window.location.host worked in preview but on the production
      // emergent.host proxy the WS upgrade sometimes failed silently,
      // forcing a slow HTTP fallback for every button press.
      const backend = process.env.REACT_APP_BACKEND_URL || window.location.origin;
      const wsUrl = backend.replace(/^http/, 'ws') + '/api/ws/overlay-state';
      ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        reconnectAttempts = 0;
        lastMessageTime = Date.now();
        setWsReady(true);
        if (heartbeatTimer) clearInterval(heartbeatTimer);
        heartbeatTimer = setInterval(() => {
          if (!ws) return;
          // Zombie WS detection — force reconnect if server hasn't sent
          // anything in 25s (same watchdog logic as the overlay page).
          if (Date.now() - lastMessageTime > 25000) {
            try { ws.close(); } catch {}
            return;
          }
          if (ws.readyState === WebSocket.OPEN) {
            try { ws.send('ping'); } catch {}
          }
        }, 8000);
      };
      ws.onmessage = () => { lastMessageTime = Date.now(); };
      ws.onclose = () => {
        setWsReady(false);
        if (heartbeatTimer) { clearInterval(heartbeatTimer); heartbeatTimer = null; }
        if (!mounted) return;
        // Cap reconnects at 5. After that we assume the environment simply
        // does not support WebSocket upgrade (common on some ingress configs)
        // and switch to permanent HTTP mode — buttons will still work fine
        // via the /overlay-state POST fallback, which broadcasts in ~150ms.
        if (reconnectAttempts >= 5) {
          wsPermanentlyOffRef.current = true;
          return;
        }
        reconnectAttempts++;
        const delay = Math.min(500 * reconnectAttempts, 3000);
        reconnectTimer = setTimeout(connect, delay);
      };
      ws.onerror = () => {};
    };

    connect();
    return () => {
      mounted = false;
      if (heartbeatTimer) clearInterval(heartbeatTimer);
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (ws) { try { ws.close(); } catch {} }
      wsRef.current = null;
    };
  }, []);

  // Get number of legs based on game type
  const getLegsCount = () => {
    if (!selectedGame) return 6;
    const gameType = selectedGame.game_type;
    if (gameType === 'V65') return 6;
    if (gameType === 'V75') return 7;
    if (gameType === 'V85') return 8;
    return 6;
  };

  // CENTRALIZED function to sync overlay state to backend
  // Sends ONLY the overrides (partial update) to avoid stale closure issues.
  // The backend merges partial updates correctly with exclude_unset=True.
  // Uses WebSocket for immediate delivery (~30-80ms latency), falls back to
  // HTTP POST if the WS isn't currently OPEN. HTTP fallback also serves as
  // a durability guarantee — if for any reason the WS message is lost, the
  // POST will still persist the state.
  const syncOverlayState = useCallback(async (overrides = {}) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      try {
        ws.send(JSON.stringify(overrides));
        return;
      } catch (e) {
        // fall through to HTTP fallback
      }
    }
    try {
      await axios.post(`${API}/overlay-state`, overrides);
    } catch (error) {
      console.error('Failed to sync overlay state:', error);
    }
  }, []);

  // Toggle webcam background
  const toggleWebcam = async () => {
    const newState = !useWebcam;
    setUseWebcam(newState);
    
    // Sync to backend - include webcam_device_id so overlay knows which camera to use
    await syncOverlayState({ 
      use_webcam: newState, 
      webcam_device_id: selectedWebcamId || '',
      audio_device_id: selectedAudioId 
    });
    
    toast.success(newState ? 'Webcam bakgrunn aktivert' : 'Grønn bakgrunn aktivert');
  };

  // ---- Loop video ----
  const fetchVideoInfo = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/video/info`);
      setVideoInfo(res.data);
    } catch (e) { /* ignore */ }
  }, []);

  // Load current loop-video info + whether it's currently shown, on mount
  useEffect(() => {
    fetchVideoInfo();
    (async () => {
      try {
        const res = await axios.get(`${API}/overlay-state`);
        if (res.data && res.data.show_video !== undefined) setShowVideo(!!res.data.show_video);
      } catch (e) { /* ignore */ }
    })();
  }, [fetchVideoInfo]);

  // Chunked upload (5MB chunks) so large videos bypass ingress body limits.
  const handleVideoUpload = async (file) => {
    if (!file) return;
    const maxBytes = 2 * 1024 * 1024 * 1024;
    if (file.size > maxBytes) { toast.error('Videoen er for stor (maks 2 GB)'); return; }
    setVideoUploading(true);
    setVideoProgress(0);
    try {
      const uploadId = (window.crypto && window.crypto.randomUUID)
        ? window.crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      const CHUNK = 5 * 1024 * 1024;
      const total = Math.ceil(file.size / CHUNK);
      for (let i = 0; i < total; i++) {
        const blob = file.slice(i * CHUNK, (i + 1) * CHUNK);
        const fd = new FormData();
        fd.append('upload_id', uploadId);
        fd.append('chunk_index', i);
        fd.append('total_chunks', total);
        fd.append('file', blob, file.name);
        await axios.post(`${API}/video/upload-chunk`, fd);
        setVideoProgress(Math.round(((i + 1) / total) * 100));
      }
      await axios.post(`${API}/video/upload-complete`, {
        upload_id: uploadId,
        filename: file.name,
        content_type: file.type || 'video/mp4',
      });
      await fetchVideoInfo();
      toast.success(`Video lastet opp: ${file.name}`);
    } catch (e) {
      console.error('Video upload failed:', e);
      toast.error(e.response?.data?.detail || 'Opplasting feilet');
    } finally {
      setVideoUploading(false);
      setVideoProgress(0);
      if (videoFileInputRef.current) videoFileInputRef.current.value = '';
    }
  };

  // Toggle fullscreen loop video on the overlay (like the other overlay toggles)
  const toggleVideo = async () => {
    if (!videoInfo?.exists) { toast.error('Last opp en video først'); return; }
    const newState = !showVideo;
    setShowVideo(newState);
    await syncOverlayState({ show_video: newState });
    toast.success(newState ? 'Loop-video vises' : 'Loop-video stoppet');
  };

  // Clear all overlays - reset to clean green screen
  const clearAllOverlays = async () => {
    await syncOverlayState({
      show_odds: false,
      show_bong: false,
      show_dual_bong: false,
      show_super: false,
      show_navnsuper: false,
      show_crawl: false,
      show_weather: false,
      show_banker: false,
      show_message: false,
      show_opening_poster: false,
      show_closing_poster: false,
      show_results: false,
      show_vignett: false,
      show_v75_popup: false,
      use_webcam: false,
      fullscreen_content: null,
      show_video: false,
    });
    setShowOddsOnOverlay(false);
    setShowBongOnOverlay(false);
    setUseWebcam(false);
    setShowVideo(false);
    toast.success('Alle overlays fjernet – ren bakgrunn');
  };

  // Change webcam device
  const changeWebcamDevice = async (deviceId) => {
    setSelectedWebcamId(deviceId);
    
    if (useWebcam) {
      await syncOverlayState({ webcam_device_id: deviceId });
      const device = webcamDevices.find(d => d.deviceId === deviceId);
      toast.success(`Kamera byttet til: ${device?.label || 'Ukjent'}`);
    }
  };

  // Change audio input device
  const changeAudioDevice = async (deviceId) => {
    setSelectedAudioId(deviceId);
    
    if (useWebcam) {
      await syncOverlayState({ audio_device_id: deviceId });
      const device = audioDevices.find(d => d.deviceId === deviceId);
      toast.success(`Lyd byttet til: ${device?.label || 'Ukjent'}`);
    }
  };

  // Start/stop preview camera (with audio)
  const togglePreviewCamera = async () => {
    if (previewActive) {
      // Stop preview
      if (previewStreamRef.current) {
        previewStreamRef.current.getTracks().forEach(t => t.stop());
        previewStreamRef.current = null;
      }
      if (previewVideoRef.current) {
        previewVideoRef.current.srcObject = null;
      }
      setPreviewActive(false);
      toast.success('Kamera stoppet');
    } else {
      // Start preview with audio
      try {
        const constraints = {
          video: selectedWebcamId ? { deviceId: { exact: selectedWebcamId } } : true,
          audio: true,
        };
        const stream = await navigator.mediaDevices.getUserMedia(constraints);
        previewStreamRef.current = stream;
        if (previewVideoRef.current) {
          previewVideoRef.current.srcObject = stream;
        }
        setPreviewActive(true);
        toast.success('Kamera startet med lyd');
      } catch (err) {
        console.error('Camera error:', err);
        // Try without audio if audio fails
        try {
          const stream = await navigator.mediaDevices.getUserMedia({
            video: selectedWebcamId ? { deviceId: { exact: selectedWebcamId } } : true,
            audio: false,
          });
          previewStreamRef.current = stream;
          if (previewVideoRef.current) {
            previewVideoRef.current.srcObject = stream;
          }
          setPreviewActive(true);
          toast.success('Kamera startet (uten lyd)');
        } catch (err2) {
          toast.error('Kunne ikke starte kamera');
        }
      }
    }
  };

  // Switch preview camera device
  const switchPreviewDevice = async (deviceId) => {
    setSelectedWebcamId(deviceId);
    if (previewActive) {
      // Restart with new device
      if (previewStreamRef.current) {
        previewStreamRef.current.getTracks().forEach(t => t.stop());
      }
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { deviceId: { exact: deviceId } },
          audio: true,
        });
        previewStreamRef.current = stream;
        if (previewVideoRef.current) {
          previewVideoRef.current.srcObject = stream;
        }
        const device = webcamDevices.find(d => d.deviceId === deviceId);
        toast.success(`Byttet til: ${device?.label || 'Ukjent'}`);
      } catch (err) {
        toast.error('Kunne ikke bytte kamera');
      }
    }
  };

  // Cleanup preview stream on unmount
  useEffect(() => {
    return () => {
      if (previewStreamRef.current) {
        previewStreamRef.current.getTracks().forEach(t => t.stop());
      }
    };
  }, []);


  // Toggle crawl on overlay
  const toggleCrawl = async () => {
    const newState = !showCrawl;
    setShowCrawl(newState);
    
    // Use RSS headlines if crawlText is empty
    let textToSend = crawlText;
    if (!textToSend && rssHeadlines.length > 0) {
      textToSend = rssHeadlines.join('  •  ');
      setCrawlText(textToSend);
    }
    
    // Sync to backend using centralized function
    await syncOverlayState({
      show_crawl: newState,
      crawl_text: textToSend,
    });
    
    toast.success(newState ? 'Crawl vises' : 'Crawl skjult');
  };

  // Update crawl text
  const updateCrawlText = async () => {
    // Determine what text to use for crawl
    let finalCrawlText = crawlText;
    if (crawlSource === 'rss' && rssHeadlines.length > 0) {
      finalCrawlText = rssHeadlines.join('  •  ');
    }
    
    // Update state BEFORE closing modal so button becomes enabled
    setCrawlText(finalCrawlText);
    setShowCrawlModal(false);
    
    // Sync to backend using centralized function
    await syncOverlayState({
      crawl_text: finalCrawlText,
      crawl_source: crawlSource,
      rss_url: rssUrl,
      rss_headlines: rssHeadlines,
    });
    
    toast.success('Crawl-tekst oppdatert');
  };

  // Helper: generate top 3 crawl text from game data
  const generateTop3Text = useCallback(() => {
    if (!gameData?.races?.length) return null;
    const gt = selectedGame?.game_type || 'V65';
    const parts = gameData.races.map((race) => {
      const sorted = [...race.horses]
        .filter(h => h.distribution > 0)
        .sort((a, b) => b.distribution - a.distribution)
        .slice(0, 3);
      if (sorted.length === 0) return null;
      const horsesStr = sorted
        .map((h, i) => `${i + 1}. ${h.name} (${h.distribution.toFixed(1)}%)`)
        .join('  \u2022  ');
      return `${gt}-${race.leg_number}: ${horsesStr}`;
    }).filter(Boolean);
    return parts.join('    |    ');
  }, [gameData, selectedGame]);

  // Auto-update crawl text when source is 'top3' and game data refreshes
  const crawlTextRef = useRef(crawlText);
  crawlTextRef.current = crawlText;
  useEffect(() => {
    if (crawlSource !== 'top3' || !showCrawl) return;
    const newText = generateTop3Text();
    if (newText && newText !== crawlTextRef.current) {
      setCrawlText(newText);
      syncOverlayState({ crawl_text: newText, show_crawl: true });
    }
  }, [gameData, crawlSource, showCrawl, generateTop3Text, syncOverlayState]);

  // Fetch RSS feed
  const fetchRssFeed = async () => {
    if (!rssUrl) {
      toast.error('Vennligst oppgi en RSS-URL');
      return;
    }
    
    setIsLoadingRss(true);
    try {
      const response = await axios.get(`${API}/rss-feed`, {
        params: { url: rssUrl }
      });
      setRssHeadlines(response.data.headlines);
      toast.success(`Hentet ${response.data.count} overskrifter fra RSS`);
    } catch (error) {
      console.error('RSS fetch error:', error);
      toast.error(error.response?.data?.detail || 'Kunne ikke hente RSS feed');
    } finally {
      setIsLoadingRss(false);
    }
  };

  // Initialize bong data when game changes - pre-fill from persisted or backend data
  useEffect(() => {
    if (selectedGame) {
      const legs = getLegsCount();
      const initialBong = {};
      // Pre-fill from persistedBongData if available and matching game type
      const source = persistedBongData?.legs || {};
      for (let i = 1; i <= legs; i++) {
        initialBong[i] = bongData[i] || source[i] || '';
      }
      setBongData(initialBong);
    }
  }, [selectedGame?.game_id]);

  // Sync persistedBongData from backend (picks up changes from /tips and /gjest pages)
  useEffect(() => {
    const syncFromBackend = async () => {
      try {
        const res = await axios.get(`${API}/overlay-state`);
        const bd = res.data?.bong_data;
        if (bd?.timestamp) {
          const currentTs = persistedBongData?.timestamp || 0;
          if (bd.timestamp > currentTs) {
            setPersistedBongData(bd);
            localStorage.setItem('atg-bong-data', JSON.stringify(bd));
            if (!showBongModal && bd.legs) {
              const legs = getLegsCount();
              const updated = {};
              for (let i = 1; i <= legs; i++) {
                updated[i] = bd.legs[i] || '';
              }
              setBongData(updated);
            }
          }
        }
        // Sync guest bong
        const bdg = res.data?.bong_data_guest;
        if (bdg?.timestamp) {
          const currentGuestTs = persistedBongDataGuest?.timestamp || 0;
          if (bdg.timestamp > currentGuestTs) {
            setPersistedBongDataGuest(bdg);
            if (bdg.ownerName) setBongNameGuest(bdg.ownerName);
            // Update guest input fields
            if (bdg.legs) {
              const legs = getLegsCount();
              const updated = {};
              for (let i = 1; i <= legs; i++) {
                updated[i] = bdg.legs[i] || '';
              }
              setBongDataGuest(updated);
            }
          }
        }
        // Sync tips name
        if (bd?.ownerName && bd.ownerName !== bongNameTips) {
          setBongNameTips(bd.ownerName);
        }
        // Sync dual bong toggle
        if (res.data?.show_dual_bong !== undefined) {
          setShowDualBong(res.data.show_dual_bong);
        }
        // Sync studio mode toggle
        if (res.data?.studio_mode !== undefined) {
          setStudioActive(res.data.studio_mode);
        }
        // Sync sponsor toggle
        if (res.data?.show_sponsors !== undefined) {
          setSponsorsActive(res.data.show_sponsors);
        }
      } catch { /* ignore */ }
    };
    const interval = setInterval(syncFromBackend, 5000);
    syncFromBackend(); // Run immediately on mount
    return () => clearInterval(interval);
  }, [persistedBongData?.timestamp, persistedBongDataGuest?.timestamp, showBongModal]);

  // Calculate number of rows - supports "/" split for small/large bong
  // "alle" keyword means all horses in that race (for stor bong)
  const calculateRows = () => {
    const legs = getLegsCount();
    let smallCombinations = 1;
    let largeCombinations = 1;
    for (let i = 1; i <= legs; i++) {
      const value = bongData[i] || '';
      const raceHorseCount = gameData?.races?.[i - 1]?.horses?.length || 0;
      if (value.includes('/')) {
        const [leftPart, rightPart] = value.split('/');
        const leftHorses = leftPart.split(',').filter(h => h.trim()).length;
        const rightTrimmed = rightPart.trim().toLowerCase();
        let totalLargeHorses;
        if (rightTrimmed === 'alle') {
          // "alle" = all horses in this race
          totalLargeHorses = raceHorseCount > 0 ? raceHorseCount : leftHorses;
        } else {
          // Deduplicate left+right and cap at race horse count
          const leftNums = new Set(leftPart.split(',').map(h => h.trim()).filter(h => h));
          const rightNums = rightPart.split(',').map(h => h.trim()).filter(h => h);
          const allNums = new Set([...leftNums, ...rightNums]);
          totalLargeHorses = raceHorseCount > 0 ? Math.min(allNums.size, raceHorseCount) : allNums.size;
        }
        if (leftHorses > 0) smallCombinations *= leftHorses;
        if (totalLargeHorses > 0) largeCombinations *= totalLargeHorses;
      } else {
        const horses = value.split(',').filter(h => h.trim()).length;
        if (horses > 0) {
          smallCombinations *= horses;
          largeCombinations *= horses;
        }
      }
    }
    return { small: smallCombinations, large: largeCombinations };
  };

  // Check if any leg has a "/" split
  const hasSplitBong = () => {
    const legs = getLegsCount();
    for (let i = 1; i <= legs; i++) {
      if ((bongData[i] || '').includes('/')) return true;
    }
    return false;
  };

  // Rikstoto pricing rules per game type (from rikstoto.no/Slik-spiller-du/Om-spillene)
  // V65: 1 kr/rad + bongavgift (1 kr per 50 kr innsats, maks 5 kr)
  // V75: 0.50 kr/rad, ingen bongavgift
  // V4:  2 kr/rad + bongavgift
  // DD:  odds-basert, bruker 1 kr/rad som tilnærming
  const getGamePricing = () => {
    const gameType = selectedGame?.game_type || 'V65';
    if (gameType === 'V75') return { pricePerRow: 0.5, hasBongavgift: false };
    if (gameType === 'V4') return { pricePerRow: 2, hasBongavgift: true };
    if (gameType === 'DD') return { pricePerRow: 1, hasBongavgift: false };
    // V65, V5, V64 etc: 1 kr/rad + bongavgift
    return { pricePerRow: 1, hasBongavgift: true };
  };

  // Bongavgift: 1 kr per påbegynte 50 kr innsats, maks 5 kr
  const calcBongavgift = (innsats) => {
    if (innsats <= 0) return 0;
    return Math.min(Math.ceil(innsats / 50), 5);
  };

  // Calculate bong cost (with Rikstoto bongavgift)
  const calculateBongCost = () => {
    const rows = calculateRows();
    const { pricePerRow, hasBongavgift } = getGamePricing();
    const rawSmall = rows.small > 0 ? rows.small * pricePerRow : 0;
    const rawLarge = rows.large > 0 ? rows.large * pricePerRow : 0;
    return {
      small: rawSmall + (hasBongavgift ? calcBongavgift(rawSmall) : 0),
      large: rawLarge + (hasBongavgift ? calcBongavgift(rawLarge) : 0),
    };
  };

  // Save bong to localStorage and backend
  const saveBong = async () => {
    // Get horse names and full data for bankers
    const horseNames = {};
    const bankerHorses = {};
    // Resolve "alle" keyword to actual horse numbers
    const resolvedLegs = {};
    if (gameData && gameData.races) {
      gameData.races.forEach((race, index) => {
        const leg = index + 1;
        const rawValue = bongData[leg] || '';
        
        // Resolve "alle" in split bong
        let resolvedValue = rawValue;
        if (rawValue.includes('/')) {
          const [leftPart, rightPart] = rawValue.split('/');
          const rightTrimmed = rightPart.trim().toLowerCase();
          if (rightTrimmed === 'alle') {
            const leftNums = leftPart.split(',').map(h => h.trim()).filter(h => h);
            const leftNumSet = new Set(leftNums.map(n => parseInt(n)));
            const remainingHorses = race.horses
              ?.filter(h => !leftNumSet.has(h.number))
              .map(h => h.number.toString()) || [];
            resolvedValue = leftPart + '/' + remainingHorses.join(',');
          }
        }
        resolvedLegs[leg] = resolvedValue;
        
        // For banker detection, use the small bong (left side of /)
        const smallPart = resolvedValue.includes('/') ? resolvedValue.split('/')[0] : resolvedValue;
        const selectedHorses = smallPart.split(',').map(h => h.trim()).filter(h => h);
        if (selectedHorses && selectedHorses.length === 1) {
          const horseNum = parseInt(selectedHorses[0]);
          const horse = race.horses?.find(h => h.number === horseNum);
          if (horse) {
            horseNames[leg] = horse.name;
            bankerHorses[leg] = {
              number: horse.number,
              name: horse.name,
              driver: horse.driver || '',
              distribution: horse.distribution || 0,
              odds: horse.odds || 0,
              win_percentage: horse.win_percentage || 0,
            };
          }
        }
      });
    }
    
    const costs = calculateBongCost();
    // Store total horse count per leg for "Alle" display in overlay
    const totalHorsesPerLeg = {};
    if (gameData?.races) {
      gameData.races.forEach((race, index) => {
        totalHorsesPerLeg[index + 1] = race.horses?.length || 0;
      });
    }
    const bongInfo = {
      gameType: selectedGame?.game_type,
      trackName: selectedGame?.track_name,
      ownerName: bongNameTips.trim() || 'Tipsekspert',
      legs: resolvedLegs,
      horseNames: horseNames,
      bankerHorses: bankerHorses,
      totalHorsesPerLeg: totalHorsesPerLeg,
      cost: costs.small,
      costLarge: hasSplitBong() ? costs.large : null,
      hasSplit: hasSplitBong(),
      timestamp: Date.now()
    };
    
    // Save to both localStorage AND state
    localStorage.setItem('atg-bong-data', JSON.stringify(bongInfo));
    setPersistedBongData(bongInfo);
    
    // Also send to backend via centralized sync
    await syncOverlayState({ bong_data: bongInfo });
    
    setShowBongModal(false);
    toast.success('Bong lagret!');
  };

  // Save guest bong
  const saveGuestBong = async () => {
    const legs = getLegsCount();
    const resolvedLegs = {};
    for (let i = 1; i <= legs; i++) {
      resolvedLegs[i] = bongDataGuest[i] || '';
    }
    // Calculate cost for guest bong
    let smallComb = 1;
    let largeComb = 1;
    let hasSplit = false;
    for (let i = 1; i <= legs; i++) {
      const value = bongDataGuest[i] || '';
      if (value.includes('/')) {
        hasSplit = true;
        const [leftPart, rightPart] = value.split('/');
        const leftHorses = leftPart.split(',').filter(h => h.trim()).length;
        const leftNums = new Set(leftPart.split(',').map(h => h.trim()).filter(h => h));
        const rightNums = rightPart.split(',').map(h => h.trim()).filter(h => h);
        const allNums = new Set([...leftNums, ...rightNums]);
        if (leftHorses > 0) smallComb *= leftHorses;
        if (allNums.size > 0) largeComb *= allNums.size;
      } else {
        const horses = value.split(',').filter(h => h.trim()).length;
        if (horses > 0) { smallComb *= horses; largeComb *= horses; }
      }
    }
    const { pricePerRow, hasBongavgift } = getGamePricing();
    const rawSmallCost = smallComb * pricePerRow;
    const rawLargeCost = largeComb * pricePerRow;
    const smallCost = rawSmallCost + (hasBongavgift ? Math.min(Math.ceil(rawSmallCost / 50), 5) : 0);
    const largeCost = rawLargeCost + (hasBongavgift ? Math.min(Math.ceil(rawLargeCost / 50), 5) : 0);
    const guestBongInfo = {
      gameType: selectedGame?.game_type,
      trackName: selectedGame?.track_name,
      ownerName: bongNameGuest.trim() || 'Gjest',
      legs: resolvedLegs,
      horseNames: {},
      bankerHorses: {},
      totalHorsesPerLeg: {},
      cost: smallCost,
      costLarge: hasSplit ? largeCost : null,
      hasSplit,
      timestamp: Date.now()
    };
    setPersistedBongDataGuest(guestBongInfo);
    await syncOverlayState({ bong_data_guest: guestBongInfo });
    setShowBongModal(false);
    toast.success('Gjestebong lagret!');
  };

  // Save navnsuper to backend
  const saveNavnsuper = async () => {
    if (!navnsuperName.trim()) {
      toast.error('Navn er påkrevd');
      return;
    }
    try {
      const response = await axios.post(`${API}/navnsupers`, {
        name: navnsuperName.trim(),
        title: navnsuperTitle.trim()
      });
      setNavnsuperList([...navnsuperList, response.data]);
      setNavnsuperName('');
      setNavnsuperTitle('');
      setShowNavnsuperModal(false);
      toast.success('Navnsuper lagret!');
    } catch (error) {
      console.error('Failed to save navnsuper:', error);
      toast.error('Kunne ikke lagre navnsuper');
    }
  };

  // Delete navnsuper from backend
  const deleteNavnsuper = async (id) => {
    try {
      await axios.delete(`${API}/navnsupers/${id}`);
      const updatedList = navnsuperList.filter(n => n.id !== id);
      setNavnsuperList(updatedList);
      if (selectedNavnsuper?.id === id) {
        setSelectedNavnsuper(null);
        setShowNavnsuper(false);
        syncOverlayState({ show_navnsuper: false, navnsuper_data: null });
      }
      toast.success('Navnsuper slettet');
    } catch (error) {
      console.error('Failed to delete navnsuper:', error);
      toast.error('Kunne ikke slette navnsuper');
    }
  };

  // Select navnsuper (but don't show yet)
  const selectNavnsuper = (navnsuper) => {
    setSelectedNavnsuper(navnsuper);
    toast.success(`Valgt: ${navnsuper.name}`);
  };

  // Toggle navnsuper visibility on overlay
  const toggleNavnsuper = async () => {
    if (!selectedNavnsuper) return;
    
    const newState = !showNavnsuper;
    
    // Hide super and odds if activating navnsuper
    let newShowOdds = showOddsOnOverlay;
    if (newState) {
      if (superActive) setSuperActive(false);
      setShowOddsOnOverlay(false);
      newShowOdds = false;
    }
    
    // When toggling navnsuper visibility, ensure we're in overlay mode
    if (displayMode === 'fullscreen') {
      setDisplayMode('overlay');
    }
    
    setShowNavnsuper(newState);
    
    // Sync to backend using centralized function
    await syncOverlayState({
      mode: 'overlay',
      show_odds: newShowOdds,
      show_super: false,
      show_navnsuper: newState,
      navnsuper_data: newState ? selectedNavnsuper : null,
    });
    
    if (newState) {
      toast.success(`Viser: ${selectedNavnsuper.name}`);
    } else {
      toast.success('Navnsuper skjult');
    }
  };

  // Load navnsuper list from backend on mount
  useEffect(() => {
    const fetchNavnsupers = async () => {
      setLoadingNavnsupers(true);
      try {
        const response = await axios.get(`${API}/navnsupers`);
        setNavnsuperList(response.data);
      } catch (error) {
        console.error('Failed to fetch navnsupers:', error);
      } finally {
        setLoadingNavnsupers(false);
      }
    };
    fetchNavnsupers();
  }, []);

  // Toggle bong visibility on overlay
  const toggleBongOnOverlay = async () => {
    const newState = !showBongOnOverlay;
    setShowBongOnOverlay(newState);
    
    // When toggling bong visibility, ensure we're in overlay mode (not fullscreen)
    if (displayMode === 'fullscreen') {
      setDisplayMode('overlay');
    }
    
    // Get full bong data from localStorage or use persisted data
    let fullBongData = persistedBongData;
    if (newState && !fullBongData) {
      const stored = localStorage.getItem('atg-bong-data');
      if (stored) {
        try {
          fullBongData = JSON.parse(stored);
          setPersistedBongData(fullBongData); // Also update state
        } catch (e) {}
      }
    }
    
    // Sync to backend using centralized function
    await syncOverlayState({
      mode: 'overlay',
      show_bong: newState,
      bong_data: fullBongData,
    });
    
    // Also update localStorage
    const stored = localStorage.getItem('atg-overlay-selection');
    let selection = {};
    if (stored) {
      try {
        selection = JSON.parse(stored);
      } catch (e) {}
    }
    selection.showBong = newState;
    selection.timestamp = Date.now();
    localStorage.setItem('atg-overlay-selection', JSON.stringify(selection));
    
    toast.success(newState ? 'Bong vises på overlay' : 'Bong skjult på overlay');
  };

  // Toggle odds visibility on overlay
  const toggleOddsOnOverlay = async () => {
    const newState = !showOddsOnOverlay;
    setShowOddsOnOverlay(newState);
    
    // When toggling odds visibility, ensure we're in overlay mode (not fullscreen)
    if (displayMode === 'fullscreen') {
      setDisplayMode('overlay');
    }
    
    // Sync to backend using centralized function
    await syncOverlayState({
      mode: 'overlay',
      show_odds: newState,
    });
    
    // Also update localStorage
    const stored = localStorage.getItem('atg-overlay-selection');
    let selection = {};
    if (stored) {
      try {
        selection = JSON.parse(stored);
      } catch (e) {}
    }
    selection.showOdds = newState;
    selection.timestamp = Date.now();
    localStorage.setItem('atg-overlay-selection', JSON.stringify(selection));
    
    toast.success(newState ? 'Odds vises på overlay' : 'Odds skjult på overlay');
  };

  // Select horse for Super/Lower Third
  const selectHorseForSuper = async (horse) => {
    setSelectedHorseForSuper(horse);
    
    // Sync to backend using centralized function
    await syncOverlayState({ super_horse: horse });
    
    // Also update localStorage
    const stored = localStorage.getItem('atg-overlay-selection');
    let selection = {};
    if (stored) {
      try {
        selection = JSON.parse(stored);
      } catch (e) {}
    }
    selection.superHorse = horse;
    selection.timestamp = Date.now();
    localStorage.setItem('atg-overlay-selection', JSON.stringify(selection));
    
    toast.success(`${horse.name} valgt for Super`);
  };

  // Toggle Super/Lower Third visibility
  const toggleSuper = async () => {
    const newState = !superActive;
    setSuperActive(newState);
    
    let newShowOdds = showOddsOnOverlay;
    let newShowBong = showBongOnOverlay;
    
    // When Super is activated, hide odds, bong and navnsuper
    if (newState) {
      setShowOddsOnOverlay(false);
      setShowBongOnOverlay(false);
      setShowNavnsuper(false);
      newShowOdds = false;
      newShowBong = false;
    }
    
    // When toggling super visibility, ensure we're in overlay mode
    if (displayMode === 'fullscreen') {
      setDisplayMode('overlay');
    }
    
    // Sync to backend using centralized function
    await syncOverlayState({
      mode: 'overlay',
      show_odds: newShowOdds,
      show_bong: newShowBong,
      show_super: newState,
      show_navnsuper: false,
      super_horse: newState ? selectedHorseForSuper : null,
    });
    
    // Also update localStorage
    const stored = localStorage.getItem('atg-overlay-selection');
    let selection = {};
    if (stored) {
      try {
        selection = JSON.parse(stored);
      } catch (e) {}
    }
    selection.showSuper = newState;
    selection.showOdds = newShowOdds;
    selection.showBong = newShowBong;
    selection.timestamp = Date.now();
    localStorage.setItem('atg-overlay-selection', JSON.stringify(selection));
    
    if (newState && selectedHorseForSuper) {
      toast.success(`Super: ${selectedHorseForSuper.name}`);
    } else {
      toast.success('Super skjult');
    }
  };

  // Toggle Banker auto-display (on/off switch for auto-banker on bong legs)
  const toggleBanker = () => {
    const newState = !bankerActive;
    setBankerActive(newState);
    if (newState) {
      toast.success('Banker-animasjon aktivert');
    } else {
      toast.success('Banker-animasjon deaktivert');
    }
  };

  // Toggle message popup on overlay
  const toggleMessage = async () => {
    const newState = !messageActive;
    setMessageActive(newState);
    const apiUrl = '';
    try {
      await fetch(`${apiUrl}/api/overlay-state`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ show_message: newState, message_text: messageText }),
      });
    } catch (e) { /* ignore */ }
    if (newState) {
      toast.success('Beskjed vises');
    } else {
      toast.success('Beskjed skjult');
    }
  };

  // Toggle V75 popup on overlay
  const toggleV75 = async () => {
    const newState = !v75Active;
    setV75Active(newState);
    try {
      await fetch(`/api/overlay-state`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ show_v75: newState, v75_text: v75Text }),
      });
    } catch (e) { /* ignore */ }
    toast.success(newState ? 'V75-melding vises' : 'V75-melding skjult');
  };

  // Clown mode
  const [clownActive, setClownActive] = useState(false);
  const clownTimerRef = useRef(null);
  const triggerClown = async () => {
    if (clownActive || kingActive) return;
    setClownActive(true);
    const wasShowingOdds = showOddsOnOverlay;
    const wasShowingBong = showBongOnOverlay;
    try {
      await fetch(`/api/overlay-state`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ show_clown: true }),
      });
    } catch (e) { /* ignore */ }
    clownTimerRef.current = setTimeout(async () => {
      setClownActive(false);
      try {
        await fetch(`/api/overlay-state`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ show_clown: false, show_odds: wasShowingOdds, show_bong: wasShowingBong }),
        });
      } catch (e) { /* ignore */ }
    }, 6000);
  };

  // King mode
  const [kingActive, setKingActive] = useState(false);
  const kingTimerRef = useRef(null);
  const triggerKing = async () => {
    if (kingActive || clownActive) return;
    setKingActive(true);
    const wasShowingOdds = showOddsOnOverlay;
    const wasShowingBong = showBongOnOverlay;
    try {
      await fetch(`/api/overlay-state`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ show_king: true }),
      });
    } catch (e) { /* ignore */ }
    kingTimerRef.current = setTimeout(async () => {
      setKingActive(false);
      try {
        await fetch(`/api/overlay-state`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ show_king: false, show_odds: wasShowingOdds, show_bong: wasShowingBong }),
        });
      } catch (e) { /* ignore */ }
    }, 6000);
  };

  // Vignett mode
  const [vignettActive, setVignettActive] = useState(false);
  const vignettTimerRef = useRef(null);
  const triggerVignett = async () => {
    if (vignettActive) return;
    setVignettActive(true);
    try {
      // When vignett starts we also clear every other graphic so that the
      // screen is a completely clean chromakey green the moment the iris
      // wipe finishes. Nothing (poster, crawl, odds, bong, weather, super,
      // etc.) should be visible after the vignett — operator starts with a
      // blank slate.
      await fetch(`/api/overlay-state`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          show_vignett: true,
          show_vignett_out: false,
          show_v75_intro: false,
          show_v75_outro: false,
          show_opening_poster: false,
          show_closing_poster: false,
          show_crawl: false,
          show_odds: false,
          show_bong: false,
          show_dual_bong: false,
          show_super: false,
          show_navnsuper: false,
          show_weather: false,
          show_banker: false,
          show_message: false,
          show_results: false,
          show_clown: false,
          show_king: false,
          show_sponsors: false,
        }),
      });
    } catch (e) { /* ignore */ }
    // Local UI state cleanup so the sidebar toggles match backend state.
    setShowOddsOnOverlay(false);
    setShowBongOnOverlay(false);
    setShowDualBong(false);
    setShowCrawl(false);
    setShowNavnsuper(false);
    setShowWeather(false);
    setPosterActive(false);
    // Vignett is ~10s (VignettOverlay component posts show_vignett:false
    // itself when the animation ends). This local timer is a safety net in
    // case /overlay isn't currently open — after ~12.8s we forcibly reset
    // the state so the button re-enables and no residual show_vignett=true
    // is left in the backend for the next overlay-open.
    vignettTimerRef.current = setTimeout(async () => {
      setVignettActive(false);
      try {
        await fetch(`/api/overlay-state`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ show_vignett: false }),
        });
      } catch (e) { /* ignore */ }
    }, 13800);
  };

  // Vignett Ut (outro) — toggle mode.
  const [vignettOutActive, setVignettOutActive] = useState(false);
  const toggleVignettOut = async () => {
    const newState = !vignettOutActive;
    setVignettOutActive(newState);
    try {
      if (newState) {
        await fetch(`/api/overlay-state`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            show_vignett_out: true,
            show_vignett: false,
            show_v75_intro: false,
            show_v75_outro: false,
            show_opening_poster: false,
            show_closing_poster: false,
            show_crawl: false,
            show_odds: false,
            show_bong: false,
            show_dual_bong: false,
            show_super: false,
            show_navnsuper: false,
            show_weather: false,
            show_banker: false,
            show_message: false,
            show_results: false,
            show_clown: false,
            show_king: false,
            show_sponsors: false,
          }),
        });
        setShowOddsOnOverlay(false);
        setShowBongOnOverlay(false);
        setShowDualBong(false);
        setShowCrawl(false);
        setShowNavnsuper(false);
        setShowWeather(false);
        setPosterActive(false);
        setV75IntroActive(false);
        setV75OutroActive(false);
      } else {
        await fetch(`/api/overlay-state`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ show_vignett_out: false }),
        });
      }
    } catch (e) { /* ignore */ }
  };

  // V75 Intro (action-preget) — fyrer én gang og går til grønn skjerm
  const [v75IntroActive, setV75IntroActive] = useState(false);
  const v75IntroTimerRef = useRef(null);
  const triggerV75Intro = async () => {
    if (v75IntroActive) return;
    setV75IntroActive(true);
    try {
      await fetch(`/api/overlay-state`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          show_v75_intro: true,
          show_vignett: false, show_vignett_out: false, show_v75_outro: false,
          show_opening_poster: false, show_closing_poster: false,
          show_crawl: false, show_odds: false, show_bong: false,
          show_dual_bong: false, show_super: false, show_navnsuper: false,
          show_weather: false, show_banker: false, show_message: false,
          show_results: false, show_clown: false, show_king: false, show_sponsors: false,
        }),
      });
      setShowOddsOnOverlay(false);
      setShowBongOnOverlay(false);
      setShowDualBong(false);
      setShowCrawl(false);
      setShowNavnsuper(false);
      setShowWeather(false);
      setPosterActive(false);
      setVignettOutActive(false);
    } catch (e) { /* ignore */ }
    v75IntroTimerRef.current = setTimeout(async () => {
      setV75IntroActive(false);
      try {
        await fetch(`/api/overlay-state`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ show_v75_intro: false }),
        });
      } catch (e) { /* ignore */ }
    }, 12800);
  };

  // V75 Outro — toggle mode
  const [v75OutroActive, setV75OutroActive] = useState(false);
  const toggleV75Outro = async () => {
    const newState = !v75OutroActive;
    setV75OutroActive(newState);
    try {
      if (newState) {
        await fetch(`/api/overlay-state`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            show_v75_outro: true,
            show_v75_intro: false,
            show_vignett: false, show_vignett_out: false,
            show_opening_poster: false, show_closing_poster: false,
            show_crawl: false, show_odds: false, show_bong: false,
            show_dual_bong: false, show_super: false, show_navnsuper: false,
            show_weather: false, show_banker: false, show_message: false,
            show_results: false, show_clown: false, show_king: false, show_sponsors: false,
          }),
        });
        setShowOddsOnOverlay(false);
        setShowBongOnOverlay(false);
        setShowDualBong(false);
        setShowCrawl(false);
        setShowNavnsuper(false);
        setShowWeather(false);
        setPosterActive(false);
        setVignettOutActive(false);
      } else {
        await fetch(`/api/overlay-state`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ show_v75_outro: false }),
        });
      }
    } catch (e) { /* ignore */ }
  };

  // Åpningsplakat (Opening Poster) mode
  const [posterActive, setPosterActive] = useState(false);
  const [posterText, setPosterText] = useState('');
  const [posterMainText, setPosterMainText] = useState('Tipssendingen starter snart');

  // Auto-update poster text when game changes
  useEffect(() => {
    if (selectedGame && gameData) {
      const dayNames = ['Søndag', 'Mandag', 'Tirsdag', 'Onsdag', 'Torsdag', 'Fredag', 'Lørdag'];
      const monthNames = ['januar', 'februar', 'mars', 'april', 'mai', 'juni', 'juli', 'august', 'september', 'oktober', 'november', 'desember'];
      const startTime = gameData.start_time || gameData.races?.[0]?.start_time || '';
      const dateStr = startTime ? startTime.split('T')[0] : selectedGame.game_id?.split('_').pop() || '';
      if (dateStr) {
        const [y, m, d] = dateStr.split('-').map(Number);
        const date = new Date(y, m - 1, d);
        const dayName = dayNames[date.getDay()];
        const monthName = monthNames[date.getMonth()];
        const newText = `${gameTypeLabel(selectedGame.game_type)} ${dayName} ${d}. ${monthName}`;
        setPosterText(newText);
      }
    }
  }, [selectedGame?.game_id, gameData?.start_time]);
  
  // Sommertravet mode - replaces all logos with animated video
  const [sommertravetActive, setSommertravetActive] = useState(false);
  const toggleSommertravet = async () => {
    const newState = !sommertravetActive;
    setSommertravetActive(newState);
    try {
      await fetch(`/api/overlay-state`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sommertravet_mode: newState }),
      });
    } catch (e) { /* ignore */ }
  };

  // Studio frame mode (Sky News-style layout with shrunken camera)
  const [studioActive, setStudioActive] = useState(false);
  // Snapshot of odds/bong panel state before entering green fullscreen (studio mode)
  const preGreenStateRef = useRef(null);
  const toggleStudioMode = async () => {
    const newState = !studioActive;
    setStudioActive(newState);
    try {
      await fetch(`/api/overlay-state`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ studio_mode: newState }),
      });
    } catch (e) { /* ignore */ }
  };

  // Normalize video control URL: without a scheme the iframe treats it as a
  // relative path and loads this app itself
  const normalizeVideoControlUrl = (raw) => {
    const trimmed = (raw || '').trim();
    if (!trimmed) return '';
    if (/^https?:\/\//i.test(trimmed)) return trimmed;
    return `http://${trimmed}`;
  };

  // Sponsor carousel (studio mode)
  const [showSponsorModal, setShowSponsorModal] = useState(false);
  const [sponsorList, setSponsorList] = useState([]);
  const [sponsorsActive, setSponsorsActive] = useState(false);
  const [sponsorUploading, setSponsorUploading] = useState(false);

  const fetchSponsors = async () => {
    try {
      const res = await axios.get(`${API}/sponsors`);
      setSponsorList(res.data || []);
    } catch (e) { /* ignore */ }
  };

  const uploadSponsorFiles = async (files) => {
    setSponsorUploading(true);
    let ok = 0;
    for (const file of files) {
      try {
        const form = new FormData();
        form.append('file', file);
        await axios.post(`${API}/sponsors`, form);
        ok++;
      } catch (e) {
        toast.error(`Kunne ikke laste opp ${file.name}: ${e.response?.data?.detail || 'ukjent feil'}`);
      }
    }
    setSponsorUploading(false);
    if (ok > 0) toast.success(`${ok} logo${ok > 1 ? 'er' : ''} lastet opp`);
    fetchSponsors();
  };

  const deleteSponsor = async (id) => {
    try {
      await axios.delete(`${API}/sponsors/${id}`);
      fetchSponsors();
    } catch (e) { toast.error('Kunne ikke slette logo'); }
  };

  const toggleSponsors = async () => {
    const newState = !sponsorsActive;
    setSponsorsActive(newState);
    await syncOverlayState({ show_sponsors: newState });
    toast.success(newState ? 'Sponsor-rull vises i studio-rammen' : 'Sponsor-rull skjult', { duration: 1500 });
  };

  const togglePoster = async () => {
    const newState = !posterActive;
    setPosterActive(newState);
    try {
      await fetch(`/api/overlay-state`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          show_opening_poster: newState,
          opening_poster_text: posterText,
          poster_main_text: posterMainText,
        }),
      });
    } catch (e) { /* ignore */ }
  };
  const updatePosterText = async (text) => {
    setPosterText(text);
    if (posterActive || closingPosterActive) {
      try {
        await fetch(`/api/overlay-state`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ opening_poster_text: text }),
        });
      } catch (e) { /* ignore */ }
    }
  };

  // Avslutningsplakat (Closing Poster) mode
  const [closingPosterActive, setClosingPosterActive] = useState(false);
  const toggleClosingPoster = async () => {
    const newState = !closingPosterActive;
    setClosingPosterActive(newState);
    // Get first race start time from gameData
    let firstRaceStartTime = '';
    if (gameData?.races?.length) {
      firstRaceStartTime = gameData.races[0].start_time || '';
    }
    try {
      await fetch(`/api/overlay-state`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          show_closing_poster: newState,
          closing_poster_start_time: firstRaceStartTime,
          opening_poster_text: posterText,
        }),
      });
    } catch (e) { /* ignore */ }
  };

  // Auto-banker: check if a leg has a banker and show it for 5 seconds
  const autoBankerTimerRef = useRef(null);
  const bankerActiveRef = useRef(false);
  useEffect(() => { bankerActiveRef.current = bankerActive; }, [bankerActive]);

  const triggerAutoBanker = useCallback(async (leg) => {
    if (!bankerActiveRef.current) return;
    if (!persistedBongData?.legs) return;
    const selection = persistedBongData.legs[leg];
    if (!selection) return;
    const horses = String(selection).split(',').map(h => h.trim().replace('.', '')).filter(Boolean);
    if (horses.length !== 1) return;
    const bankerHorseData = persistedBongData.bankerHorses?.[leg];
    if (!bankerHorseData) return;
    if (autoBankerTimerRef.current) clearTimeout(autoBankerTimerRef.current);
    const apiUrl = '';
    try {
      await fetch(`${apiUrl}/api/overlay-state`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ show_banker: true, banker_horse: bankerHorseData }),
      });
    } catch (e) { /* ignore */ }
    autoBankerTimerRef.current = setTimeout(async () => {
      try {
        await fetch(`${apiUrl}/api/overlay-state`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ show_banker: false }),
        });
      } catch (e) { /* ignore */ }
    }, 5000);
  }, [persistedBongData]);

  // Fetch and toggle weather display
  const toggleWeather = async () => {
    if (showWeather) {
      // Hide weather
      setShowWeather(false);
      
      // Sync to backend using centralized function
      await syncOverlayState({
        mode: 'overlay',
        show_weather: false,
        weather_data: null,
      });
      
      toast.success('Vær skjult');
    } else {
      // Fetch and show weather
      if (!selectedGame) return;
      
      setLoadingWeather(true);
      try {
        const gameDate = weatherDate || (selectedGame.start_time 
          ? selectedGame.start_time.split('T')[0] 
          : new Date().toISOString().split('T')[0]);
        const response = await axios.get(`${API}/weather/${encodeURIComponent(selectedGame.track_name)}/${gameDate}`);
        setWeatherData(response.data);
        setShowWeather(true);
        
        // Also hide odds and bong locally
        setShowOddsOnOverlay(false);
        setShowBongOnOverlay(false);
        setSuperActive(false);
        setShowNavnsuper(false);
        
        // Sync to backend using centralized function
        await syncOverlayState({
          mode: 'overlay',
          show_odds: false,
          show_bong: false,
          show_super: false,
          show_weather: true,
          show_navnsuper: false,
          weather_data: response.data,
        });
        
        toast.success(`Vær for ${selectedGame.track_name}`);
      } catch (error) {
        console.error("Failed to fetch weather:", error);
        toast.error("Kunne ikke hente værdata");
      } finally {
        setLoadingWeather(false);
      }
    }
  };

  // Toggle results overlay - directly shows/hides results using saved tips
  const toggleResults = async () => {
    if (resultsActive) {
      try {
        await fetch(`${API}/overlay-state`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ show_results: false }),
        });
        setResultsActive(false);
        toast.success('Resultater skjult');
      } catch (err) {
        toast.error('Feil ved skjuling av resultater');
      }
    } else {
      setResultsLoading(true);
      try {
        const poolType = selectedGame?.game_type || 'V65';
        const resp = await fetch(`${API}/previous-results/${poolType}`);
        const data = await resp.json();
        if (data.error) {
          toast.error(data.error);
          setResultsLoading(false);
          return;
        }
        data.bong_tips = resultsBong;
        await fetch(`${API}/overlay-state`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ show_results: true, results_data: data }),
        });
        setResultsActive(true);
        toast.success(`Viser ${poolType} resultater`);
      } catch (err) {
        toast.error('Kunne ikke hente resultater');
      }
      setResultsLoading(false);
    }
  };


  const toggleLoop = () => {
    if (loopActive) {
      // Stop loop
      if (loopIntervalRef.current) {
        clearInterval(loopIntervalRef.current);
        loopIntervalRef.current = null;
      }
      setLoopActive(false);
      
      // Go back to chromakey overlay mode with hidden odds (clean green screen)
      setDisplayMode('overlay');
      setShowOddsOnOverlay(false);
      setShowBongOnOverlay(false);
      setFullscreenContent(null);
      
      syncOverlayState({
        mode: 'overlay',
        show_odds: false,
        show_bong: false,
        fullscreen_content: null,
      });
      
      toast.success('Sløyfe stoppet - ren grønn bakgrunn');
    } else {
      // Start loop
      if (!selectedGame || !gameData?.races?.length) return;
      
      // Seed refs with current selection so the interval reads freshest data.
      loopSelectedGameRef.current = selectedGame;
      loopGameDataRef.current = gameData;
      loopLegIndexRef.current = 0;
      
      // Activate fullscreen odds mode
      setDisplayMode('fullscreen');
      setFullscreenContent('odds');
      
      const updateLeg = () => {
        const game = loopSelectedGameRef.current;
        const gd = loopGameDataRef.current;
        if (!game || !gd?.races?.length) return;
        
        const totalLegs = game.legs;
        const legNum = (loopLegIndexRef.current % totalLegs) + 1;
        setSelectedLeg(legNum);
        
        // Find the correct race data for this leg
        const race = gd.races.find(r => r.leg_number === legNum) || gd.races[loopLegIndexRef.current % gd.races.length];
        setRaceData(race);
        
        // Sync full state with correct race_data to overlay
        syncOverlayState({
          game_id: game.game_id,
          game_type: game.game_type,
          track_name: game.track_name,
          leg: legNum,
          mode: 'fullscreen',
          fullscreen_content: 'odds',
          show_odds: true,
          race_data: race,
        });
        
        loopLegIndexRef.current++;
      };
      
      // Run immediately
      updateLeg();
      
      // Then every 15 seconds
      loopIntervalRef.current = setInterval(updateLeg, 15000);
      
      setLoopActive(true);
      toast.success(`Sløyfe startet - ${selectedGame.legs} løp (${selectedGame.game_type}), 15 sek hver`);
    }
  };

  // Keep loop refs in sync with the *currently* selected game so switching
  // V65 → V75 → DD mid-loop makes the sløyfe follow the new game type
  // starting from leg 1 of that game on the next tick.
  useEffect(() => {
    const prevGameId = loopSelectedGameRef.current?.game_id;
    loopSelectedGameRef.current = selectedGame;
    loopGameDataRef.current = gameData;
    if (loopActive && selectedGame && prevGameId && prevGameId !== selectedGame.game_id) {
      loopLegIndexRef.current = 0; // restart from leg 1 of the newly selected game
    }
  }, [selectedGame, gameData, loopActive]);

  // Clean up loop on unmount
  useEffect(() => {
    return () => {
      if (loopIntervalRef.current) {
        clearInterval(loopIntervalRef.current);
      }
    };
  }, []);

  // Keyboard shortcuts for Stream Deck integration
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Ignore if typing in an input field
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
        return;
      }

      // F1 - Toggle Odds
      if (e.key === 'F1') {
        e.preventDefault();
        toggleOddsOnOverlay();
      }
      // F2 - Toggle Bong
      else if (e.key === 'F2') {
        e.preventDefault();
        toggleBongOnOverlay();
      }
      // F3 - Toggle Super (if horse selected)
      else if (e.key === 'F3') {
        e.preventDefault();
        if (selectedHorseForSuper) {
          toggleSuper();
        }
      }
      // F4 - Toggle Weather
      else if (e.key === 'F4') {
        e.preventDefault();
        toggleWeather();
      }
      // F5 - Toggle Navnsuper (if selected)
      else if (e.key === 'F5') {
        e.preventDefault();
        if (selectedNavnsuper) {
          toggleNavnsuper();
        }
      }
      // F6 - Toggle Loop
      else if (e.key === 'F6') {
        e.preventDefault();
        toggleLoop();
      }
      // F7 - Toggle Webcam background
      else if (e.key === 'F7') {
        e.preventDefault();
        toggleWebcam();
      }
      // Shift + 1-9 or 0 - Select horse by number
      else if (e.shiftKey && ((e.key >= '1' && e.key <= '9') || e.key === '0')) {
        e.preventDefault();
        const horseNum = e.key === '0' ? 10 : parseInt(e.key);
        if (raceData && raceData.horses) {
          const horse = raceData.horses.find(h => h.number === horseNum);
          if (horse) {
            selectHorseForSuper(horse);
          } else {
            toast.error(`Hest ${horseNum} finnes ikke i dette løpet`);
          }
        }
      }
      // 1-9 - Select leg/race (without shift)
      else if (!e.shiftKey && e.key >= '1' && e.key <= '9') {
        const legNum = parseInt(e.key);
        const maxLegs = getLegsCount();
        if (legNum <= maxLegs && selectedGame) {
          e.preventDefault();
          setSelectedLeg(legNum);
          broadcastSelection(selectedGame.game_id, legNum);
          toast.success(`Løp ${legNum} valgt`);
        }
      }
      // Arrow Right - Next leg
      else if (e.key === 'ArrowRight') {
        e.preventDefault();
        const maxLegs = getLegsCount();
        if (selectedLeg < maxLegs && selectedGame) {
          const newLeg = selectedLeg + 1;
          setSelectedLeg(newLeg);
          broadcastSelection(selectedGame.game_id, newLeg);
        }
      }
      // Arrow Left - Previous leg
      else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        if (selectedLeg > 1 && selectedGame) {
          const newLeg = selectedLeg - 1;
          setSelectedLeg(newLeg);
          broadcastSelection(selectedGame.game_id, newLeg);
        }
      }
      // Arrow Up - Previous horse
      else if (e.key === 'ArrowUp') {
        e.preventDefault();
        if (raceData && raceData.horses && raceData.horses.length > 0) {
          const sortedHorses = [...raceData.horses].sort((a, b) => a.number - b.number);
          if (!selectedHorseForSuper) {
            selectHorseForSuper(sortedHorses[sortedHorses.length - 1]);
          } else {
            const currentIndex = sortedHorses.findIndex(h => h.number === selectedHorseForSuper.number);
            const prevIndex = currentIndex > 0 ? currentIndex - 1 : sortedHorses.length - 1;
            selectHorseForSuper(sortedHorses[prevIndex]);
          }
        }
      }
      // Arrow Down - Next horse
      else if (e.key === 'ArrowDown') {
        e.preventDefault();
        if (raceData && raceData.horses && raceData.horses.length > 0) {
          const sortedHorses = [...raceData.horses].sort((a, b) => a.number - b.number);
          if (!selectedHorseForSuper) {
            selectHorseForSuper(sortedHorses[0]);
          } else {
            const currentIndex = sortedHorses.findIndex(h => h.number === selectedHorseForSuper.number);
            const nextIndex = currentIndex < sortedHorses.length - 1 ? currentIndex + 1 : 0;
            selectHorseForSuper(sortedHorses[nextIndex]);
          }
        }
      }
      // Escape - Hide all overlays
      else if (e.key === 'Escape') {
        e.preventDefault();
        setShowOddsOnOverlay(false);
        setShowBongOnOverlay(false);
        setSuperActive(false);
        setShowWeather(false);
        setShowNavnsuper(false);
        syncOverlayState({
          show_odds: false,
          show_bong: false,
          show_super: false,
          show_weather: false,
          show_navnsuper: false,
        });
        toast.success('Alle overlegg skjult');
      }
      // + (Plus) - Add next bong leg to selection
      else if (e.key === '+' || e.key === '=') {
        e.preventDefault();
        if (showBongOnOverlay && persistedBongData) {
          const totalLegs = persistedBongData.gameType === 'V75' ? 7 : persistedBongData.gameType === 'V85' ? 8 : 6;
          const maxSelected = bongSelectedLegs.length > 0 ? Math.max(...bongSelectedLegs) : 0;
          if (maxSelected < totalLegs) {
            const newLeg = maxSelected + 1;
            const newLegs = [...bongSelectedLegs, newLeg];
            setBongSelectedLegs(newLegs);
            syncOverlayState({ bong_display_leg: newLegs });
            triggerAutoBanker(newLeg);
            toast.success(`Lagt til løp ${newLeg}`);
          }
        }
      }
      // - (Minus) - Remove last bong leg from selection
      else if (e.key === '-') {
        e.preventDefault();
        if (showBongOnOverlay && persistedBongData) {
          if (bongSelectedLegs.length > 0) {
            const maxSelected = Math.max(...bongSelectedLegs);
            const newLegs = bongSelectedLegs.filter(l => l !== maxSelected);
            setBongSelectedLegs(newLegs);
            syncOverlayState({ bong_display_leg: newLegs.length > 0 ? newLegs : null });
            toast.success(newLegs.length > 0 ? `Fjernet løp ${maxSelected}` : 'Viser alle løp');
          }
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedGame, selectedLeg, showOddsOnOverlay, showBongOnOverlay, superActive, showWeather, showNavnsuper, selectedHorseForSuper, selectedNavnsuper, loopActive, raceData, bongSelectedLegs, persistedBongData, syncOverlayState, triggerAutoBanker]);

  // Broadcast selection to overlay via localStorage
  const broadcastSelection = useCallback(async (gameId, leg, mode = null, skipBackendSync = false) => {
    if (gameId) {
      const currentStored = localStorage.getItem('atg-overlay-selection');
      let currentMode = 'overlay';
      if (currentStored) {
        try {
          const parsed = JSON.parse(currentStored);
          currentMode = parsed.mode || 'overlay';
        } catch (e) {}
      }
      
      const finalMode = mode !== null ? mode : currentMode;
      
      // Only sync to backend if not skipping (skip on initial auto-select to preserve existing backend state)
      if (!skipBackendSync) {
        await syncOverlayState({
          game_id: gameId,
          leg: leg,
          mode: finalMode,
        });
      }
      
      // Also update localStorage for same-machine fallback
      const selection = {
        gameId,
        leg,
        mode: finalMode,
        showOdds: showOddsOnOverlay,
        showBong: showBongOnOverlay,
        showSuper: superActive,
        showWeather: showWeather,
        showNavnsuper: showNavnsuper,
        fullscreenContent: fullscreenContent,
        superHorse: selectedHorseForSuper,
        weatherData: weatherData,
        navnsuperData: selectedNavnsuper,
        timestamp: Date.now()
      };
      localStorage.setItem('atg-overlay-selection', JSON.stringify(selection));
    }
  }, [showOddsOnOverlay, showBongOnOverlay, superActive, showWeather, showNavnsuper, fullscreenContent, selectedHorseForSuper, weatherData, selectedNavnsuper, syncOverlayState]);

  // Kjente spilltyper sortert etter «størrelse» (for pene knapper)
  const KNOWN_GAME_ORDER = ['V75', 'V85', 'V86', 'V65', 'V64', 'V5', 'V4', 'DD', 'LD'];
  // Vis pen etikett for spilltype (ALL -> «Alle løp»)
  const gameTypeLabel = (gt) => (gt === 'ALL' || gt === 'ALLE') ? 'Alle løp' : (gt || '');
  // Pen løp-etikett: «Alle løp» viser «Løp N», ellers «V4-N»
  const legLabel = (gt, n) => (gt === 'ALL' || gt === 'ALLE') ? `Løp ${n}` : `${gt}-${n}`;
  const gameTypesForDay = (day) => {
    const products = [...new Set((day?.pools || []).map((p) => p.product).filter(Boolean))];
    return products.sort((a, b) => {
      const ia = KNOWN_GAME_ORDER.indexOf(a); const ib = KNOWN_GAME_ORDER.indexOf(b);
      return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
    });
  };

  // Hent løpsprogram for en bestemt dag + spilltype. gameType 'ALL' = alle løp.
  const loadSorlandetGame = useCallback(async (dayKey, gameType) => {
    if (!dayKey || !gameType) return;
    setRikstotoLoading(true);
    try {
      const url = gameType === 'ALL'
        ? `${API}/rikstoto/track/${dayKey}/all-races`
        : `${API}/rikstoto/track/${dayKey}/game/${gameType}`;
      const response = await axios.get(url, { timeout: 40000 });
      const rikstotoGame = response.data;
      const numLegs = rikstotoGame.races?.length || 6;
      const convertedGame = {
        game_id: rikstotoGame.game_id,
        game_type: rikstotoGame.game_type || 'ALLE',
        track_name: rikstotoGame.track_name,
        start_time: rikstotoGame.start_time,
        races: rikstotoGame.races,
        legs: numLegs,
        source: 'rikstoto',
      };
      setAvailableGames([convertedGame]);
      setSelectedGame(convertedGame);
      setGameData(rikstotoGame);
      setSelectedLeg(1);
      const firstRace = rikstotoGame.races && rikstotoGame.races.length > 0 ? rikstotoGame.races[0] : null;
      setRaceData(firstRace);
      setGamesLastUpdate(new Date());
      setLastUpdate(new Date());
      await syncOverlayState({
        game_id: convertedGame.game_id,
        game_type: convertedGame.game_type,
        track_name: convertedGame.track_name,
        leg: 1,
        race_data: firstRace,
      });
      toast.success(`Hentet ${gameTypeLabel(convertedGame.game_type)} – ${convertedGame.track_name}`);
    } catch (error) {
      console.error("Failed to load Sørlandet game:", error);
      toast.error(error.response?.data?.detail || "Kunne ikke hente løpsprogram");
    } finally {
      setRikstotoLoading(false);
    }
  }, [syncOverlayState]);

  // Wrapper brukt av spilltype-knappene og oppdater-knappen (laster for valgt dag)
  const fetchRikstotoSorlandet = useCallback((gameType = 'V65') => {
    const dayKey = selectedDayKey || sorlandetDays[0]?.raceday_key;
    return loadSorlandetGame(dayKey, gameType);
  }, [selectedDayKey, sorlandetDays, loadSorlandetGame]);

  // Bytt dag -> last automatisk det første tilgjengelige spillet (eller alle løp)
  const handleDaySelect = useCallback((dayKey) => {
    setSelectedDayKey(dayKey);
    const day = sorlandetDays.find((d) => d.raceday_key === dayKey);
    const gts = gameTypesForDay(day);
    loadSorlandetGame(dayKey, gts[0] || 'ALL');
  }, [sorlandetDays, loadSorlandetGame]);

  // Fetch game data - ONLY updates odds data, does NOT touch overlay state toggles
  const fetchGameData = useCallback(async (isInitialLoad = false) => {
    if (!selectedGame) return;
    
    // Use existing gameData - update race data for selected leg
    if (gameData && gameData.races) {
      const race = gameData.races.find(r => r.leg_number === selectedLeg || r.race_number === selectedLeg + (gameData.races[0]?.race_number || 1) - 1);
      setRaceData(race || null);
    }
  }, [selectedGame, selectedLeg, gameData]);

  // Initial load — hent løpsdager for Sørlandet, auto-last tilgjengelig spill (m/retry)
  useEffect(() => {
    let retryCount = 0;
    const maxRetries = 6;
    let retryTimer = null;
    let mounted = true;

    const tryFetch = async () => {
      if (!mounted) return;
      try {
        const response = await axios.get(`${API}/rikstoto/sorlandet/racedays`, { timeout: 12000 });
        const days = (response.data && response.data.racedays) || [];
        if (!mounted) return;
        setSorlandetDays(days);
        if (days.length > 0) {
          const day = days[0];
          setSelectedDayKey(day.raceday_key);
          const gts = gameTypesForDay(day);
          loadSorlandetGame(day.raceday_key, gts[0] || 'ALL');
        }
      } catch (err) {
        retryCount++;
        if (mounted && retryCount < maxRetries) {
          const delay = Math.min(3000 + retryCount * 2000, 10000);
          retryTimer = setTimeout(tryFetch, delay);
        }
      }
    };

    tryFetch();

    return () => {
      mounted = false;
      if (retryTimer) clearTimeout(retryTimer);
    };
  }, []);

  // Fetch game data when selection changes
  useEffect(() => {
    if (selectedGame) {
      // First load is initial load
      fetchGameData(!initialStateLoaded.current);
    }
  }, [selectedGame, selectedLeg, fetchGameData]);

  // Get legs for selected game
  const legs = selectedGame ? Array.from({ length: selectedGame.legs }, (_, i) => i + 1) : [];

  // Handle leg selection
  const handleLegSelect = async (leg) => {
    setSelectedLeg(leg);
    
    // Update raceData from existing gameData
    let newRaceData = null;
    if (gameData && gameData.races) {
      const startRace = gameData.races[0]?.race_number || 1;
      const race = gameData.races.find(r => r.leg_number === leg || r.race_number === startRace + leg - 1);
      newRaceData = race || null;
      setRaceData(newRaceData);
    }
    
    if (selectedGame) {
      // Sync to backend with updated race_data
      await syncOverlayState({
        game_id: selectedGame.game_id,
        leg: leg,
        race_data: newRaceData,
      });
    }
  };

  // Handle game selection
  const handleGameSelect = async (gameId) => {
    const game = availableGames.find(g => g.game_id === gameId);
    setSelectedGame(game);
    setSelectedLeg(1);
    if (game && gameData?.races) {
      const firstRace = gameData.races[0] || null;
      setRaceData(firstRace);
      await syncOverlayState({
        game_id: game.game_id,
        game_type: game.game_type,
        track_name: game.track_name,
        leg: 1,
        race_data: firstRace,
      });
    }
  };

  // Copy overlay URL
  const copyOverlayUrl = () => {
    const overlayUrl = `${window.location.origin}/overlay`;
    navigator.clipboard.writeText(overlayUrl);
    toast.success("Overlay URL kopiert!");
  };

  // Load test data for overlay testing without Rikstoto
  const loadTestData = async () => {
    const testRaces = [
      { race_number: 1, leg_number: 1, distance: 2140, start_method: "Volte", start_time: new Date(Date.now() + 3600000).toISOString(), horses: [
        { number: 3, name: "Not Over The Top", driver: "Kjell Vidar Braaten (T)", distribution: 43.7, odds: 2.15, win_percentage: 12, shoe_changes: "" },
        { number: 6, name: "E.C. Dream S.C.", driver: "Jonny Kongevold", distribution: 30.0, odds: 3.64, win_percentage: 8, shoe_changes: "" },
        { number: 4, name: "Gaio Noark", driver: "Marielle Bråthen (T)", distribution: 10.8, odds: 8.90, win_percentage: 5, shoe_changes: "Sko" },
        { number: 2, name: "Venus de Milo", driver: "Kenneth Handeland (T)", distribution: 8.3, odds: 12.40, win_percentage: 3, shoe_changes: "" },
        { number: 5, name: "Too Young", driver: "Johan Kringeland Eriksen (T)", distribution: 4.0, odds: 24.00, win_percentage: 2, shoe_changes: "" },
        { number: 7, name: "Be My Love", driver: "Arne Daniel Halvorsen (S)", distribution: 1.5, odds: 65.00, win_percentage: 1, shoe_changes: "" },
        { number: 1, name: "Okidoki M.V.", driver: "Jeanett T. Vereide", distribution: 0.7, odds: 99.00, win_percentage: 0, shoe_changes: "" },
      ]},
      { race_number: 2, leg_number: 2, distance: 1609, start_method: "Auto", start_time: new Date(Date.now() + 5400000).toISOString(), horses: [
        { number: 1, name: "King of Speed", driver: "Erik Solberg (T)", distribution: 35.2, odds: 2.80, win_percentage: 15, shoe_changes: "" },
        { number: 5, name: "Brave Heart", driver: "Magnus Teien Gundersen", distribution: 22.1, odds: 4.50, win_percentage: 10, shoe_changes: "" },
        { number: 3, name: "Flash Gordon", driver: "Frode Hamre", distribution: 18.4, odds: 5.20, win_percentage: 7, shoe_changes: "Sko" },
        { number: 8, name: "Lucky Strike", driver: "Ulf Ohlsson (S)", distribution: 9.5, odds: 10.50, win_percentage: 4, shoe_changes: "" },
        { number: 2, name: "Diamond Boy", driver: "Geir Vegard Gundersen", distribution: 7.3, odds: 13.00, win_percentage: 3, shoe_changes: "" },
        { number: 6, name: "Silver Moon", driver: "Tom Erik Solberg", distribution: 4.1, odds: 24.00, win_percentage: 2, shoe_changes: "" },
        { number: 4, name: "Power Play", driver: "Dag-Sveinung Dalen", distribution: 2.2, odds: 45.00, win_percentage: 1, shoe_changes: "" },
        { number: 7, name: "Nordic Star", driver: "Vidar Hop", distribution: 1.2, odds: 80.00, win_percentage: 0, shoe_changes: "" },
      ]},
      { race_number: 3, leg_number: 3, distance: 2100, start_method: "Volte", start_time: new Date(Date.now() + 7200000).toISOString(), horses: [
        { number: 4, name: "Thunder Road", driver: "Øystein Tjomsland", distribution: 28.6, odds: 3.50, win_percentage: 11, shoe_changes: "" },
        { number: 1, name: "Storm Warning", driver: "Erlend Rennesvik", distribution: 25.3, odds: 3.90, win_percentage: 9, shoe_changes: "Sko" },
        { number: 7, name: "Night Rider", driver: "Hans Chr. Holm", distribution: 15.8, odds: 6.30, win_percentage: 6, shoe_changes: "" },
        { number: 2, name: "Fast Forward", driver: "Bjørn Goop (S)", distribution: 12.1, odds: 8.20, win_percentage: 5, shoe_changes: "" },
        { number: 6, name: "Cool Runnings", driver: "Are Bye", distribution: 8.9, odds: 11.00, win_percentage: 3, shoe_changes: "" },
        { number: 3, name: "Dark Horse", driver: "Robert Bergh (S)", distribution: 5.4, odds: 18.00, win_percentage: 2, shoe_changes: "" },
        { number: 5, name: "Wild Card", driver: "Atle Solhus", distribution: 3.9, odds: 25.00, win_percentage: 1, shoe_changes: "" },
      ]},
      { race_number: 4, leg_number: 4, distance: 2640, start_method: "Auto", start_time: new Date(Date.now() + 9000000).toISOString(), horses: [
        { number: 2, name: "Marathon Man", driver: "Vidar Hop", distribution: 31.5, odds: 3.10, win_percentage: 14, shoe_changes: "" },
        { number: 5, name: "Long Distance", driver: "Geir Vegard Gundersen", distribution: 24.8, odds: 4.00, win_percentage: 8, shoe_changes: "" },
        { number: 1, name: "Iron Horse", driver: "Frode Hamre", distribution: 16.2, odds: 6.10, win_percentage: 6, shoe_changes: "Sko" },
        { number: 8, name: "Steady Pace", driver: "Magnus Teien Gundersen", distribution: 10.5, odds: 9.50, win_percentage: 4, shoe_changes: "" },
        { number: 3, name: "Endurance", driver: "Erik Solberg (T)", distribution: 7.8, odds: 12.80, win_percentage: 3, shoe_changes: "" },
        { number: 6, name: "Mile King", driver: "Bjørn Goop (S)", distribution: 5.1, odds: 19.00, win_percentage: 2, shoe_changes: "" },
        { number: 4, name: "Going Strong", driver: "Tom Erik Solberg", distribution: 2.8, odds: 35.00, win_percentage: 1, shoe_changes: "" },
        { number: 7, name: "Last Lap", driver: "Dag-Sveinung Dalen", distribution: 1.3, odds: 75.00, win_percentage: 0, shoe_changes: "" },
      ]},
      { race_number: 5, leg_number: 5, distance: 1609, start_method: "Auto", start_time: new Date(Date.now() + 10800000).toISOString(), horses: [
        { number: 6, name: "Rocket Fuel", driver: "Ørjan Kihlström (S)", distribution: 38.9, odds: 2.50, win_percentage: 16, shoe_changes: "" },
        { number: 1, name: "Turbo Charge", driver: "Frode Hamre", distribution: 20.4, odds: 4.80, win_percentage: 9, shoe_changes: "" },
        { number: 3, name: "Speed Demon", driver: "Bjørn Goop (S)", distribution: 14.7, odds: 6.70, win_percentage: 5, shoe_changes: "Sko" },
        { number: 4, name: "Nitro Boost", driver: "Erik Solberg (T)", distribution: 11.2, odds: 8.90, win_percentage: 4, shoe_changes: "" },
        { number: 2, name: "Quick Silver", driver: "Vidar Hop", distribution: 8.3, odds: 12.00, win_percentage: 3, shoe_changes: "" },
        { number: 5, name: "Jet Stream", driver: "Geir Vegard Gundersen", distribution: 4.5, odds: 22.00, win_percentage: 2, shoe_changes: "" },
        { number: 7, name: "Hyper Drive", driver: "Hans Chr. Holm", distribution: 2.0, odds: 50.00, win_percentage: 1, shoe_changes: "" },
      ]},
      { race_number: 6, leg_number: 6, distance: 2140, start_method: "Volte", start_time: new Date(Date.now() + 12600000).toISOString(), horses: [
        { number: 1, name: "Final Chapter", driver: "Øystein Tjomsland", distribution: 33.4, odds: 2.95, win_percentage: 13, shoe_changes: "" },
        { number: 4, name: "Grand Finale", driver: "Magnus Teien Gundersen", distribution: 21.7, odds: 4.60, win_percentage: 8, shoe_changes: "Sko" },
        { number: 7, name: "Last Dance", driver: "Erlend Rennesvik", distribution: 17.3, odds: 5.70, win_percentage: 6, shoe_changes: "" },
        { number: 2, name: "Closing Time", driver: "Bjørn Goop (S)", distribution: 11.9, odds: 8.40, win_percentage: 4, shoe_changes: "" },
        { number: 5, name: "Encore", driver: "Ulf Ohlsson (S)", distribution: 8.1, odds: 12.30, win_percentage: 3, shoe_changes: "" },
        { number: 3, name: "Swan Song", driver: "Frode Hamre", distribution: 4.6, odds: 21.00, win_percentage: 2, shoe_changes: "" },
        { number: 6, name: "Take a Bow", driver: "Are Bye", distribution: 3.0, odds: 33.00, win_percentage: 1, shoe_changes: "" },
      ]},
    ];

    const testGame = { game_id: "test_v65", game_type: "V65", track_name: "Sørlandet", legs: 6 };

    setGameData({ game_type: "V65", track_name: "Sørlandet", races: testRaces });
    setRaceData(testRaces[0]);
    setSelectedGame(testGame);
    setAvailableGames([testGame]);
    setSelectedLeg(1);

    await syncOverlayState({
      game_id: "test_v65",
      game_type: "V65",
      track_name: "Sørlandet",
      leg: 1,
      race_data: testRaces[0],
      display_mode: "overlay",
    });
    toast.success("Testdata lastet! 6 løp med hester klare.", { duration: 3000 });
  };

  // Copy vMix overlay URL
  const copyVmixUrl = () => {
    const vmixUrl = `${window.location.origin}/vmix`;
    navigator.clipboard.writeText(vmixUrl);
    toast.success("vMix/OBS URL kopiert!");
  };

  // Open vMix overlay in new window
  const openVmixOverlay = () => {
    const vmixUrl = `${window.location.origin}/vmix`;
    const vmixWindow = window.open(
      vmixUrl,
      "atg_vmix_overlay",
      "width=1920,height=1080,menubar=no,toolbar=no,location=no,status=no"
    );
    if (vmixWindow) {
      vmixWindow.focus();
      toast.success('vMix/OBS overlay åpnet - transparent bakgrunn', { duration: 3000 });
    }
  };

  // Open overlay in new window
  // Open overlay in new window - tries to open fullscreen
  // Note: Browser cannot control which monitor window opens on. 
  // User needs to drag to screen 2 first time, then browser remembers position.
  const openOverlay = () => {
    const overlayUrl = `${window.location.origin}/overlay?autoFullscreen=true`;
    // Open window with specific features for better fullscreen support
    const overlayWindow = window.open(
      overlayUrl, 
      "atg_overlay", 
      "width=1920,height=1080,menubar=no,toolbar=no,location=no,status=no"
    );
    
    if (overlayWindow) {
      // Focus the new window
      overlayWindow.focus();
      toast.success('Overlay åpnet - dobbeltklikk for fullskjerm, eller dra til skjerm 2', { duration: 3000 });
    }
  };

  // Toggle fullscreen mode on overlay
  const toggleFullscreen = () => {
    const newMode = displayMode === 'overlay' ? 'fullscreen' : 'overlay';
    setDisplayMode(newMode);
    if (selectedGame) {
      broadcastSelection(selectedGame.game_id, selectedLeg, newMode);
    }
    toast.success(newMode === 'fullscreen' ? 'Fullskjerm aktivert' : 'Chromakey aktivert', { duration: 1500 });
  };

  // Show fullscreen odds
  const showFullscreenOdds = async () => {
    if (displayMode === 'fullscreen' && fullscreenContent === 'odds') {
      // Turn off fullscreen - restore previous show_odds state (was false before entering fullscreen)
      setDisplayMode('overlay');
      
      // When exiting fullscreen, keep odds hidden to avoid animation
      // User can manually show odds again if they want
      setShowOddsOnOverlay(false);
      
      // Sync to backend using centralized function
      await syncOverlayState({
        mode: 'overlay',
        fullscreen_content: null,
        show_odds: false,  // Keep odds hidden when exiting fullscreen
        ...(useWipe ? { wipe_token: Date.now() } : {}),
      });
      
      const stored = localStorage.getItem('atg-overlay-selection');
      let selection = {};
      if (stored) {
        try {
          selection = JSON.parse(stored);
        } catch (e) {}
      }
      selection.mode = 'overlay';
      selection.timestamp = Date.now();
      localStorage.setItem('atg-overlay-selection', JSON.stringify(selection));
      toast.success('Chromakey aktivert', { duration: 1500 });
    } else {
      setDisplayMode('fullscreen');
      setFullscreenContent('odds');
      
      // Sync to backend using centralized function
      await syncOverlayState({
        mode: 'fullscreen',
        show_odds: true,
        show_bong: false,
        show_super: false,
        show_weather: false,
        show_navnsuper: false,
        fullscreen_content: 'odds',
        ...(useWipe ? { wipe_token: Date.now() } : {}),
      });
      
      const stored = localStorage.getItem('atg-overlay-selection');
      let selection = {};
      if (stored) {
        try {
          selection = JSON.parse(stored);
        } catch (e) {}
      }
      selection.mode = 'fullscreen';
      selection.fullscreenContent = 'odds';
      selection.timestamp = Date.now();
      localStorage.setItem('atg-overlay-selection', JSON.stringify(selection));
      toast.success('Fullskjerm odds aktivert', { duration: 1500 });
    }
  };

  // Show fullscreen odds with green chromakey background
  const showFullscreenOddsGreen = async () => {
    if (displayMode === 'fullscreen' && fullscreenContent === 'odds_green') {
      // Turn off
      setDisplayMode('overlay');
      setFullscreenContent(null);
      // In studio mode: restore odds/bong panels to how they were before green
      const restore = studioActive && preGreenStateRef.current ? preGreenStateRef.current : null;
      preGreenStateRef.current = null;
      setShowOddsOnOverlay(restore ? restore.show_odds : false);
      setShowBongOnOverlay(restore ? restore.show_bong : false);
      await syncOverlayState({
        mode: 'overlay',
        fullscreen_content: null,
        show_odds: restore ? restore.show_odds : false,
        show_bong: restore ? restore.show_bong : showBongOnOverlay,
        ...(useWipe ? { wipe_token: Date.now() } : {}),
      });
      toast.success('Chromakey aktivert', { duration: 1500 });
    } else {
      // Snapshot panel state before entering green (only from non-green state)
      if (studioActive && !(displayMode === 'fullscreen' && (fullscreenContent === 'odds_green' || fullscreenContent === 'bong_green'))) {
        preGreenStateRef.current = { show_odds: showOddsOnOverlay, show_bong: showBongOnOverlay };
      }
      setDisplayMode('fullscreen');
      setFullscreenContent('odds_green');
      await syncOverlayState({
        mode: 'fullscreen',
        fullscreen_content: 'odds_green',
        show_odds: true,
        show_bong: false,
        show_super: false,
        show_weather: false,
        show_navnsuper: false,
        ...(useWipe ? { wipe_token: Date.now() } : {}),
      });
      toast.success('Fullskjerm odds (grønn) aktivert', { duration: 1500 });
    }
  };

  // Show fullscreen bong with green chromakey background
  const showFullscreenBongGreen = async () => {
    if (displayMode === 'fullscreen' && fullscreenContent === 'bong_green') {
      setDisplayMode('overlay');
      setFullscreenContent(null);
      // In studio mode: restore odds/bong panels to how they were before green
      const restore = studioActive && preGreenStateRef.current ? preGreenStateRef.current : null;
      preGreenStateRef.current = null;
      setShowBongOnOverlay(restore ? restore.show_bong : false);
      setShowOddsOnOverlay(restore ? restore.show_odds : showOddsOnOverlay);
      await syncOverlayState({
        mode: 'overlay',
        fullscreen_content: null,
        show_bong: restore ? restore.show_bong : false,
        show_odds: restore ? restore.show_odds : showOddsOnOverlay,
        ...(useWipe ? { wipe_token: Date.now() } : {}),
      });
      toast.success('Chromakey aktivert', { duration: 1500 });
    } else {
      // Snapshot panel state before entering green (only from non-green state)
      if (studioActive && !(displayMode === 'fullscreen' && (fullscreenContent === 'odds_green' || fullscreenContent === 'bong_green'))) {
        preGreenStateRef.current = { show_odds: showOddsOnOverlay, show_bong: showBongOnOverlay };
      }
      setDisplayMode('fullscreen');
      setFullscreenContent('bong_green');
      await syncOverlayState({
        mode: 'fullscreen',
        fullscreen_content: 'bong_green',
        show_bong: true,
        show_odds: false,
        show_super: false,
        show_weather: false,
        show_navnsuper: false,
        ...(useWipe ? { wipe_token: Date.now() } : {}),
      });
      toast.success('Fullskjerm bong (grønn) aktivert', { duration: 1500 });
    }
  };

  // Show fullscreen bong
  const showFullscreenBong = async () => {
    if (displayMode === 'fullscreen' && fullscreenContent === 'bong') {
      // Turn off fullscreen
      setDisplayMode('overlay');
      
      // When exiting fullscreen, keep bong hidden to avoid animation
      setShowBongOnOverlay(false);
      
      // Sync to backend using centralized function
      await syncOverlayState({
        mode: 'overlay',
        fullscreen_content: null,
        show_bong: false,  // Keep bong hidden when exiting fullscreen
        ...(useWipe ? { wipe_token: Date.now() } : {}),
      });
      
      const stored = localStorage.getItem('atg-overlay-selection');
      let selection = {};
      if (stored) {
        try {
          selection = JSON.parse(stored);
        } catch (e) {}
      }
      selection.mode = 'overlay';
      selection.timestamp = Date.now();
      localStorage.setItem('atg-overlay-selection', JSON.stringify(selection));
      toast.success('Chromakey aktivert', { duration: 1500 });
    } else {
      // Get bong data from state first, then localStorage, then backend
      let fullBongData = persistedBongData;
      
      if (!fullBongData) {
        const storedBong = localStorage.getItem('atg-bong-data');
        if (storedBong) {
          try {
            fullBongData = JSON.parse(storedBong);
            setPersistedBongData(fullBongData);
          } catch (e) {}
        }
      }
      
      // If still not found, try to get from backend
      if (!fullBongData) {
        try {
          const stateResponse = await axios.get(`${API}/overlay-state`);
          if (stateResponse.data.bong_data) {
            fullBongData = stateResponse.data.bong_data;
            setPersistedBongData(fullBongData);
          }
        } catch (e) {
          console.error('Failed to get bong data from backend:', e);
        }
      }
      
      if (!fullBongData) {
        toast.error('Ingen bong lagret. Lag en bong først.');
        return;
      }
      
      setDisplayMode('fullscreen');
      setFullscreenContent('bong');
      
      // Sync to backend using centralized function
      await syncOverlayState({
        mode: 'fullscreen',
        show_odds: false,
        show_bong: true,
        show_super: false,
        show_weather: false,
        show_navnsuper: false,
        fullscreen_content: 'bong',
        bong_data: fullBongData,
        ...(useWipe ? { wipe_token: Date.now() } : {}),
      });
      
      const stored = localStorage.getItem('atg-overlay-selection');
      let selection = {};
      if (stored) {
        try {
          selection = JSON.parse(stored);
        } catch (e) {}
      }
      selection.mode = 'fullscreen';
      selection.fullscreenContent = 'bong';
      selection.timestamp = Date.now();
      localStorage.setItem('atg-overlay-selection', JSON.stringify(selection));
      toast.success('Fullskjerm bong aktivert', { duration: 1500 });
    }
  };

  return (
    <div className="control-panel flex min-h-screen" data-testid="control-panel">
      {/* Left 75%: Control Panel */}
      <div style={{ width: '75%', display: 'flex', minHeight: '100vh' }}>
      {/* Sidebar */}
      <Sidebar
        connected={connected}
        overlayHealth={overlayHealth}
        lastUpdate={lastUpdate}
        gamesLastUpdate={gamesLastUpdate}
        availableGames={availableGames}
        selectedGame={selectedGame}
        selectedLeg={selectedLeg}
        rikstotoLoading={rikstotoLoading}
        sorlandetDays={sorlandetDays}
        selectedDayKey={selectedDayKey}
        availableGameTypes={gameTypesForDay(sorlandetDays.find((d) => d.raceday_key === selectedDayKey))}
        onDaySelect={handleDaySelect}
        onGameTypeSelect={fetchRikstotoSorlandet}
        onGameSelect={handleGameSelect}
        onLegSelect={handleLegSelect}
        onRefresh={fetchRikstotoSorlandet}
      />

      {/* Main Content */}
      <div className="flex-1 flex flex-col">
        {/* Header */}
        <div className="p-4 border-b border-[#334155] flex items-center justify-between bg-[#0F172A]">
          <div>
            <h2 className="font-barlow text-2xl font-bold uppercase tracking-tight">
              {selectedGame ? `${gameTypeLabel(selectedGame.game_type)} - ${selectedGame.track_name}` : 'Velg et spill'}
            </h2>
            {raceData && (
              <p className="text-sm text-[#94A3B8] mt-1">
                Løp {raceData.race_number} • {raceData.distance}m • Start: {new Date(raceData.start_time).toLocaleTimeString('nb-NO', { hour: '2-digit', minute: '2-digit' })}
              </p>
            )}
          </div>
          <div className="flex items-center gap-3">
            <Button
              data-testid="manual-refresh"
              variant="outline"
              size="sm"
              onClick={() => loadSorlandetGame(selectedDayKey || sorlandetDays[0]?.raceday_key, selectedGame?.game_type || availableGames[0]?.game_type || 'ALL')}
              disabled={loading || rikstotoLoading}
              className="bg-transparent border-[#334155] text-white hover:bg-[#1E293B]"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${(loading || rikstotoLoading) ? 'animate-spin' : ''}`} />
              Oppdater
            </Button>
          </div>
        </div>

        {/* Control Buttons - Two Column Layout */}
        <div className="p-4 border-b border-[#334155]">
          <div className="grid grid-cols-2 gap-4">
            
            {/* LEFT COLUMN: Production Controls (show/hide/trigger) */}
            <div className="space-y-3">
              <div className="text-xs text-[#94A3B8] uppercase tracking-wider font-semibold mb-2">Produksjon</div>
              
              {/* Odds & Bong toggles */}
              <div className="flex items-center gap-2 flex-wrap">
                <Button
                  data-testid="toggle-odds"
                  variant="outline"
                  size="sm"
                  onClick={toggleOddsOnOverlay}
                  disabled={!selectedGame}
                  className={`border-[#334155] text-white hover:bg-[#1E293B] ${!showOddsOnOverlay ? 'bg-[#1E293B]' : 'bg-transparent'}`}
                >
                  {showOddsOnOverlay ? <Eye className="w-4 h-4 mr-2" /> : <EyeOff className="w-4 h-4 mr-2" />}
                  {showOddsOnOverlay ? 'Skjul Odds' : 'Vis Odds'}
                </Button>
                <Button
                  data-testid="show-bong"
                  variant="outline"
                  size="sm"
                  onClick={toggleBongOnOverlay}
                  disabled={!selectedGame}
                  className={`border-[#334155] text-white hover:bg-[#1E293B] ${showBongOnOverlay ? 'bg-[#1E293B]' : 'bg-transparent'}`}
                >
                  <FileText className="w-4 h-4 mr-2" />
                  {showBongOnOverlay ? 'Skjul Bong' : 'Vis Bong'}
                </Button>
                {persistedBongData && (
                  <div className="flex items-center gap-1 bg-[#1E293B] rounded-md p-1">
                    {Array.from({ length: persistedBongData.gameType === 'V75' ? 7 : persistedBongData.gameType === 'V85' ? 8 : 6 }, (_, i) => i + 1).map((leg) => (
                      <button
                        key={leg}
                        data-testid={`bong-leg-${leg}`}
                        onClick={() => {
                          const isSelected = bongSelectedLegs.includes(leg);
                          const newLegs = isSelected 
                            ? bongSelectedLegs.filter(l => l !== leg)
                            : [...bongSelectedLegs, leg].sort((a, b) => a - b);
                          setBongSelectedLegs(newLegs);
                          syncOverlayState({ bong_display_leg: newLegs.length > 0 ? newLegs : null });
                          if (!isSelected) triggerAutoBanker(leg);
                        }}
                        className={`px-2 py-1 text-xs font-mono rounded transition-all ${
                          bongSelectedLegs.includes(leg)
                            ? 'bg-blue-600 text-white' 
                            : 'text-[#94A3B8] hover:bg-[#334155] hover:text-white'
                        }`}
                      >
                        {leg}
                      </button>
                    ))}
                    <button
                      data-testid="bong-leg-all"
                      onClick={() => {
                        setBongSelectedLegs([]);
                        syncOverlayState({ bong_display_leg: null });
                        toast.success('Viser alle løp');
                      }}
                      className={`px-2 py-1 text-xs font-mono rounded transition-all ${
                        bongSelectedLegs.length === 0
                          ? 'bg-blue-600 text-white' 
                          : 'text-[#94A3B8] hover:bg-[#334155] hover:text-white'
                      }`}
                    >
                      Alle
                    </button>
                  </div>
                )}
              </div>

              {/* Bong status indicators */}
              {(persistedBongData || persistedBongDataGuest) && (
                <div className="flex items-center gap-3 text-xs">
                  {persistedBongData && (
                    <div className="flex items-center gap-1.5 bg-[#1E293B] rounded px-2 py-1">
                      <div className="w-2 h-2 rounded-full bg-green-500" />
                      <span className="text-[#94A3B8]">{persistedBongData.ownerName || 'Tipsekspert'}</span>
                      <span className="text-white font-mono">{persistedBongData.cost}kr</span>
                    </div>
                  )}
                  {persistedBongDataGuest && (
                    <div className="flex items-center gap-1.5 bg-[#1E293B] rounded px-2 py-1">
                      <div className="w-2 h-2 rounded-full bg-blue-500" />
                      <span className="text-[#94A3B8]">{persistedBongDataGuest.ownerName || 'Gjest'}</span>
                      <span className="text-white font-mono">{persistedBongDataGuest.cost}kr</span>
                    </div>
                  )}
                  <button
                    data-testid="dual-bong-toggle"
                    onClick={() => {
                      const next = !showDualBong;
                      setShowDualBong(next);
                      syncOverlayState({ show_dual_bong: next });
                      toast.success(next ? 'Dobbel bong aktivert' : 'Enkel bong aktivert');
                    }}
                    className={`px-2 py-1 text-xs rounded border transition-all ${
                      showDualBong
                        ? 'bg-blue-600 border-blue-500 text-white'
                        : 'bg-transparent border-[#334155] text-[#94A3B8] hover:text-white hover:border-[#64748B]'
                    }`}
                  >
                    {showDualBong ? '2 bonger' : '1 bong'}
                  </button>
                </div>
              )}

              {/* Show/hide overlays */}
              <div className="flex items-center gap-2 flex-wrap">
                <Button
                  data-testid="super-toggle"
                  variant="outline"
                  size="sm"
                  onClick={toggleSuper}
                  disabled={!selectedGame || !selectedHorseForSuper}
                  className={`border-[#334155] text-white hover:bg-[#1E293B] ${superActive ? 'bg-yellow-600 hover:bg-yellow-700 border-yellow-600' : 'bg-transparent'}`}
                >
                  <Trophy className="w-4 h-4 mr-2" />
                  Super
                </Button>
                <Button
                  data-testid="message-toggle"
                  variant="outline"
                  size="sm"
                  onClick={toggleMessage}
                  disabled={!messageText.trim()}
                  className={`border-[#334155] text-white hover:bg-[#1E293B] ${messageActive ? 'bg-purple-600 hover:bg-purple-700 border-purple-600' : 'bg-transparent'}`}
                >
                  <Eye className="w-4 h-4 mr-2" />
                  {messageActive ? 'Skjul Beskjed' : 'Vis Beskjed'}
                </Button>
                <Button
                  data-testid="v75-toggle"
                  variant="outline"
                  size="sm"
                  onClick={toggleV75}
                  disabled={!v75Text.trim()}
                  className={`border-[#334155] text-white hover:bg-[#1E293B] ${v75Active ? 'bg-yellow-600 hover:bg-yellow-700 border-yellow-600' : 'bg-transparent'}`}
                >
                  <Eye className="w-4 h-4 mr-2" />
                  {v75Active ? 'Skjul V75' : 'Vis V75'}
                </Button>
              </div>

              {/* Navnsuper, Crawl, Vær */}
              <div className="flex items-center gap-2 flex-wrap">
                <div className="relative">
                  <select
                    data-testid="navnsuper-dropdown"
                    value={selectedNavnsuper?.id || ''}
                    onChange={(e) => {
                      const id = e.target.value;
                      const ns = navnsuperList.find(n => n.id === id);
                      if (ns) {
                        selectNavnsuper(ns);
                      }
                    }}
                    className={`h-9 px-3 py-1 text-sm rounded-md border cursor-pointer appearance-none pr-8 ${selectedNavnsuper ? 'bg-purple-900 border-purple-900 text-white' : 'bg-transparent border-[#334155] text-white'}`}
                    style={{minWidth: '180px'}}
                  >
                    <option value="" disabled className="bg-[#1E293B] text-[#94A3B8]">
                      Velg Navnsuper
                    </option>
                    {navnsuperList.map((ns) => (
                      <option 
                        key={ns.id} 
                        value={ns.id}
                        className="bg-[#1E293B] text-white"
                      >
                        {ns.title ? `${ns.name} - ${ns.title}` : ns.name}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="w-4 h-4 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none text-white" />
                </div>
                <Button
                  data-testid="toggle-navnsuper"
                  variant="outline"
                  size="sm"
                  onClick={toggleNavnsuper}
                  disabled={!selectedNavnsuper}
                  className={`border-[#334155] text-white hover:bg-[#1E293B] ${showNavnsuper ? 'bg-purple-600 hover:bg-purple-700 border-purple-600' : 'bg-transparent'}`}
                >
                  <Eye className="w-4 h-4 mr-2" />
                  {showNavnsuper ? 'Skjul' : 'Vis'} Navnsuper
                </Button>
                <Button
                  data-testid="toggle-crawl"
                  variant="outline"
                  size="sm"
                  onClick={toggleCrawl}
                  disabled={!crawlText && rssHeadlines.length === 0}
                  className={`border-[#334155] text-white hover:bg-[#1E293B] ${showCrawl ? 'bg-cyan-600 hover:bg-cyan-700 border-cyan-600' : 'bg-transparent'}`}
                >
                  <Eye className="w-4 h-4 mr-2" />
                  {showCrawl ? 'Skjul' : 'Vis'} Crawl
                </Button>
                <Button
                  data-testid="weather-toggle"
                  variant="outline"
                  size="sm"
                  onClick={toggleWeather}
                  disabled={!selectedGame || loadingWeather}
                  className={`border-[#334155] text-white hover:bg-[#1E293B] ${showWeather ? 'bg-sky-600 hover:bg-sky-700 border-sky-600' : 'bg-transparent'}`}
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" />
                  </svg>
                  Vær
                </Button>
                <input
                  data-testid="weather-date"
                  type="date"
                  value={weatherDate}
                  onChange={(e) => setWeatherDate(e.target.value)}
                  className="bg-[#0F172A] border border-[#334155] text-white text-xs rounded px-2 py-1.5 h-8"
                  style={{ colorScheme: 'dark' }}
                />
              </div>

              {/* Triggers & special */}
              <div className="flex items-center gap-2 flex-wrap">
                <Button
                  data-testid="clown-trigger"
                  variant="outline"
                  size="sm"
                  onClick={triggerClown}
                  disabled={clownActive}
                  className={`border-[#334155] text-white hover:bg-[#1E293B] ${clownActive ? 'bg-red-600 hover:bg-red-700 border-red-600' : 'bg-transparent'}`}
                >
                  <span className="mr-2">&#x1F921;</span>
                  Klovn
                </Button>
                <Button
                  data-testid="king-trigger"
                  variant="outline"
                  size="sm"
                  onClick={triggerKing}
                  disabled={kingActive || clownActive}
                  className={`border-[#334155] text-white hover:bg-[#1E293B] ${kingActive ? 'bg-yellow-600 hover:bg-yellow-700 border-yellow-600' : 'bg-transparent'}`}
                >
                  <span className="mr-2">&#x1F451;</span>
                  Konge
                </Button>
                <Button
                  data-testid="vignett-trigger"
                  variant="outline"
                  size="sm"
                  onClick={triggerVignett}
                  disabled={vignettActive}
                  className={`border-[#334155] text-white hover:bg-[#1E293B] ${vignettActive ? 'bg-blue-600 hover:bg-blue-700 border-blue-600' : 'bg-transparent'}`}
                >
                  <Play className="w-4 h-4 mr-2" />
                  Vignett
                </Button>
                <Button
                  data-testid="vignett-out-toggle"
                  variant="outline"
                  size="sm"
                  onClick={toggleVignettOut}
                  className={`border-[#334155] text-white hover:bg-[#1E293B] ${vignettOutActive ? 'bg-blue-600 hover:bg-blue-700 border-blue-600' : 'bg-transparent'}`}
                >
                  <Play className="w-4 h-4 mr-2 rotate-180" />
                  Vignett Ut
                </Button>
                <Button
                  data-testid="v75-intro-trigger"
                  variant="outline"
                  size="sm"
                  onClick={triggerV75Intro}
                  disabled={v75IntroActive}
                  className={`border-[#334155] text-white hover:bg-[#1E293B] ${v75IntroActive ? 'bg-red-600 hover:bg-red-700 border-red-600' : 'bg-transparent'}`}
                >
                  <Play className="w-4 h-4 mr-2" />
                  V75 Intro
                </Button>
                <Button
                  data-testid="v75-outro-toggle"
                  variant="outline"
                  size="sm"
                  onClick={toggleV75Outro}
                  className={`border-[#334155] text-white hover:bg-[#1E293B] ${v75OutroActive ? 'bg-red-600 hover:bg-red-700 border-red-600' : 'bg-transparent'}`}
                >
                  <Play className="w-4 h-4 mr-2 rotate-180" />
                  V75 Uttro
                </Button>
                <Button
                  data-testid="poster-toggle"
                  variant="outline"
                  size="sm"
                  onClick={togglePoster}
                  className={`border-[#334155] text-white hover:bg-[#1E293B] ${posterActive ? 'bg-purple-600 hover:bg-purple-700 border-purple-600' : 'bg-transparent'}`}
                >
                  <Image className="w-4 h-4 mr-2" />
                  Åpningsplakat
                </Button>
                <Button
                  data-testid="closing-poster-toggle"
                  variant="outline"
                  size="sm"
                  onClick={toggleClosingPoster}
                  className={`border-[#334155] text-white hover:bg-[#1E293B] ${closingPosterActive ? 'bg-purple-600 hover:bg-purple-700 border-purple-600' : 'bg-transparent'}`}
                >
                  <Image className="w-4 h-4 mr-2" />
                  Avslutningsplakat
                </Button>
                <Button
                  data-testid="sommertravet-toggle"
                  variant="outline"
                  size="sm"
                  onClick={toggleSommertravet}
                  className={`border-[#334155] text-white hover:bg-[#1E293B] ${sommertravetActive ? 'bg-orange-500 hover:bg-orange-600 border-orange-500' : 'bg-transparent'}`}
                >
                  <Sun className="w-4 h-4 mr-2" />
                  Sommertravet
                </Button>
                <Button
                  data-testid="studio-mode-toggle"
                  variant="outline"
                  size="sm"
                  onClick={toggleStudioMode}
                  className={`border-[#334155] text-white hover:bg-[#1E293B] ${studioActive ? 'bg-cyan-600 hover:bg-cyan-700 border-cyan-600' : 'bg-transparent'}`}
                >
                  <Monitor className="w-4 h-4 mr-2" />
                  Studio-ramme
                </Button>
                <Button
                  data-testid="loop-toggle"
                  variant="outline"
                  size="sm"
                  onClick={toggleLoop}
                  disabled={!selectedGame}
                  className={`border-[#334155] text-white hover:bg-[#1E293B] ${loopActive ? 'bg-green-600 hover:bg-green-700 border-green-600' : 'bg-transparent'}`}
                >
                  <RotateCw className={`w-4 h-4 mr-2 ${loopActive ? 'animate-spin' : ''}`} />
                  Sløyfe
                </Button>
                <Button
                  data-testid="results-toggle"
                  variant="outline"
                  size="sm"
                  onClick={toggleResults}
                  disabled={resultsLoading}
                  className={`border-[#334155] text-white hover:bg-[#1E293B] ${resultsActive ? 'bg-amber-600 hover:bg-amber-700 border-amber-600' : 'bg-transparent'}`}
                >
                  <Trophy className="w-4 h-4 mr-2" />
                  {resultsLoading ? 'Henter...' : 'Vis Resultater'}
                </Button>
              </div>

              {/* Åpningsplakat text inputs */}
              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-2">
                  <span className="text-xs text-[#94A3B8] whitespace-nowrap w-20">Hovedtekst:</span>
                  <Input
                    data-testid="poster-main-text-input"
                    value={posterMainText}
                    onChange={(e) => setPosterMainText(e.target.value)}
                    placeholder="Tipssendingen starter snart"
                    className="h-8 bg-[#1E293B] border-[#334155] text-white text-sm flex-1 max-w-xs"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-[#94A3B8] whitespace-nowrap w-20">Undertekst:</span>
                  <Input
                    data-testid="poster-text-input"
                    value={posterText}
                    onChange={(e) => updatePosterText(e.target.value)}
                    placeholder="F.eks. V65 Mandag 8. juni"
                    className="h-8 bg-[#1E293B] border-[#334155] text-white text-sm flex-1 max-w-xs"
                  />
                </div>
              </div>

              {/* Fullscreen modes */}
              <div className="flex items-center gap-2 flex-wrap">
                <Button
                  data-testid="fullscreen-odds"
                  variant="outline"
                  size="sm"
                  onClick={showFullscreenOdds}
                  disabled={!selectedGame}
                  className={`border-[#334155] text-white hover:bg-[#1E293B] ${displayMode === 'fullscreen' && fullscreenContent === 'odds' ? 'bg-[#1E293B]' : 'bg-transparent'}`}
                >
                  <Maximize className="w-4 h-4 mr-2" />
                  Fullskjerm Odds
                </Button>
                <Button
                  data-testid="fullscreen-odds-green"
                  variant="outline"
                  size="sm"
                  onClick={showFullscreenOddsGreen}
                  disabled={!selectedGame}
                  className={`border-[#334155] text-white hover:bg-[#1E293B] ${displayMode === 'fullscreen' && fullscreenContent === 'odds_green' ? 'bg-green-700' : 'bg-transparent'}`}
                >
                  <Maximize className="w-4 h-4 mr-2" />
                  Odds Grønn
                </Button>
                <Button
                  data-testid="fullscreen-bong"
                  variant="outline"
                  size="sm"
                  onClick={showFullscreenBong}
                  disabled={!selectedGame}
                  className={`border-[#334155] text-white hover:bg-[#1E293B] ${displayMode === 'fullscreen' && fullscreenContent === 'bong' ? 'bg-[#1E293B]' : 'bg-transparent'}`}
                >
                  <Maximize className="w-4 h-4 mr-2" />
                  Fullskjerm Bong
                </Button>
                <Button
                  data-testid="fullscreen-bong-green"
                  variant="outline"
                  size="sm"
                  onClick={showFullscreenBongGreen}
                  disabled={!selectedGame}
                  className={`border-[#334155] text-white hover:bg-[#1E293B] ${displayMode === 'fullscreen' && fullscreenContent === 'bong_green' ? 'bg-green-700' : 'bg-transparent'}`}
                >
                  <Maximize className="w-4 h-4 mr-2" />
                  Bong Grønn
                </Button>
                <Button
                  data-testid="toggle-wipe"
                  variant="outline"
                  size="sm"
                  onClick={toggleUseWipe}
                  title="Slå gull-wipe overgang av/på for fullskjerm odds og bong"
                  className={`border-[#334155] text-white hover:bg-[#1E293B] ${useWipe ? 'bg-amber-600 hover:bg-amber-700 border-amber-600' : 'bg-transparent'}`}
                >
                  <Film className="w-4 h-4 mr-2" />
                  {useWipe ? 'Wipe: På' : 'Wipe: Av'}
                </Button>
              </div>
            </div>

            {/* RIGHT COLUMN: Content Creation (modals/text input) */}
            <div className="space-y-3">
              <div className="text-xs text-[#94A3B8] uppercase tracking-wider font-semibold mb-2">Innhold</div>
              
              <div className="flex items-center gap-2 flex-wrap">
                <Button
                  data-testid="create-bong"
                  variant="outline"
                  size="sm"
                  onClick={() => setShowBongModal(true)}
                  disabled={!selectedGame}
                  className="bg-transparent border-[#334155] text-white hover:bg-[#1E293B]"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Lag Bong
                </Button>
                <Button
                  data-testid="banker-toggle"
                  variant="outline"
                  size="sm"
                  onClick={toggleBanker}
                  disabled={!selectedGame}
                  className={`border-[#334155] text-white hover:bg-[#1E293B] ${bankerActive ? 'bg-amber-600 hover:bg-amber-700 border-amber-600' : 'bg-transparent'}`}
                >
                  <Star className="w-4 h-4 mr-2" />
                  Banker
                </Button>
                <Button
                  data-testid="message-edit"
                  variant="outline"
                  size="sm"
                  onClick={() => setShowMessageModal(true)}
                  className="border-[#334155] text-white hover:bg-[#1E293B] bg-transparent"
                >
                  <MessageSquare className="w-4 h-4 mr-2" />
                  Beskjed
                </Button>
                <Button
                  data-testid="sponsor-edit"
                  variant="outline"
                  size="sm"
                  onClick={() => { setShowSponsorModal(true); fetchSponsors(); }}
                  className={`border-[#334155] text-white hover:bg-[#1E293B] ${sponsorsActive ? 'bg-emerald-600 hover:bg-emerald-700 border-emerald-600' : 'bg-transparent'}`}
                >
                  <Image className="w-4 h-4 mr-2" />
                  Sponsorer
                </Button>
                <Button
                  data-testid="v75-edit"
                  variant="outline"
                  size="sm"
                  onClick={() => setShowV75Modal(true)}
                  className="border-[#334155] text-white hover:bg-[#1E293B] bg-transparent"
                >
                  <MessageSquare className="w-4 h-4 mr-2" />
                  V75
                </Button>
              </div>

              <div className="flex items-center gap-2 flex-wrap">
                <Button
                  data-testid="create-navnsuper"
                  variant="outline"
                  size="sm"
                  onClick={() => setShowNavnsuperModal(true)}
                  className="bg-transparent border-[#334155] text-white hover:bg-[#1E293B]"
                >
                  <User className="w-4 h-4 mr-2" />
                  Lag Navnsuper
                </Button>
                <Button
                  data-testid="edit-crawl"
                  variant="outline"
                  size="sm"
                  onClick={() => setShowCrawlModal(true)}
                  className="bg-transparent border-[#334155] text-white hover:bg-[#1E293B]"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Crawl Tekst
                </Button>
                <Button
                  data-testid="edit-results-bong"
                  variant="outline"
                  size="sm"
                  onClick={() => setShowResultsModal(true)}
                  className="bg-transparent border-[#334155] text-white hover:bg-[#1E293B]"
                >
                  <Trophy className="w-4 h-4 mr-2" />
                  Resultat-tips
                </Button>
              </div>

              {/* Video Input */}
              <div className="mt-4 pt-3 border-t border-[#334155]">
                <div className="text-xs text-[#94A3B8] uppercase tracking-wider font-semibold mb-2">Video Input</div>
                <div className="flex items-center gap-2 flex-wrap">
                  <Button
                    data-testid="toggle-webcam"
                    variant="outline"
                    size="sm"
                    onClick={toggleWebcam}
                    className={`border-[#334155] text-white hover:bg-[#1E293B] ${useWebcam ? 'bg-orange-600 hover:bg-orange-700 border-orange-600' : 'bg-transparent'}`}
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                    {useWebcam ? 'Grønn BG' : 'Webcam BG'}
                  </Button>
                  {webcamDevices.length > 0 && (
                    <div className="relative">
                      <select
                        data-testid="webcam-dropdown"
                        value={selectedWebcamId}
                        onChange={(e) => changeWebcamDevice(e.target.value)}
                        className={`h-9 px-3 py-1 text-sm rounded-md border cursor-pointer appearance-none pr-8 ${useWebcam ? 'bg-orange-900 border-orange-900 text-white' : 'bg-transparent border-[#334155] text-white'}`}
                        style={{minWidth: '160px', maxWidth: '220px'}}
                      >
                        {webcamDevices.map((device, index) => (
                          <option 
                            key={device.deviceId} 
                            value={device.deviceId}
                            className="bg-[#1E293B] text-white"
                          >
                            {device.label || `Kamera ${index + 1}`}
                          </option>
                        ))}
                      </select>
                      <ChevronDown className="w-4 h-4 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none text-white" />
                    </div>
                  )}
                  {audioDevices.length > 0 && (
                    <div className="relative">
                      <select
                        data-testid="audio-dropdown"
                        value={selectedAudioId}
                        onChange={(e) => changeAudioDevice(e.target.value)}
                        className={`h-9 px-3 py-1 text-sm rounded-md border cursor-pointer appearance-none pr-8 ${useWebcam ? 'bg-orange-900 border-orange-900 text-white' : 'bg-transparent border-[#334155] text-white'}`}
                        style={{minWidth: '160px', maxWidth: '220px'}}
                      >
                        {audioDevices.map((device, index) => (
                          <option 
                            key={device.deviceId} 
                            value={device.deviceId}
                            className="bg-[#1E293B] text-white"
                          >
                            {device.label || `Mikrofon ${index + 1}`}
                          </option>
                        ))}
                      </select>
                      <ChevronDown className="w-4 h-4 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none text-white" />
                    </div>
                  )}
                </div>
              </div>

              {/* Loop Video */}
              <div className="mt-4 pt-3 border-t border-[#334155]">
                <div className="text-xs text-[#94A3B8] uppercase tracking-wider font-semibold mb-2">Loop-video (fullskjerm m/ lyd)</div>
                <input
                  ref={videoFileInputRef}
                  data-testid="video-file-input"
                  type="file"
                  accept="video/mp4,video/webm,video/quicktime,video/*"
                  className="hidden"
                  onChange={(e) => handleVideoUpload(e.target.files?.[0])}
                />
                <div className="flex items-center gap-2 flex-wrap">
                  <Button
                    data-testid="upload-video-btn"
                    variant="outline"
                    size="sm"
                    disabled={videoUploading}
                    onClick={() => videoFileInputRef.current && videoFileInputRef.current.click()}
                    className="bg-transparent border-[#334155] text-white hover:bg-[#1E293B]"
                  >
                    {videoUploading ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Laster opp… {videoProgress}%</>
                    ) : (
                      <><Upload className="w-4 h-4 mr-2" /> {videoInfo?.exists ? 'Bytt video' : 'Last opp video'}</>
                    )}
                  </Button>
                  <Button
                    data-testid="toggle-video-btn"
                    variant="outline"
                    size="sm"
                    disabled={!videoInfo?.exists || videoUploading}
                    onClick={toggleVideo}
                    className={`border-[#334155] text-white hover:bg-[#1E293B] ${showVideo ? 'bg-purple-600 hover:bg-purple-700 border-purple-600' : 'bg-transparent'}`}
                  >
                    <Film className="w-4 h-4 mr-2" />
                    {showVideo ? 'Stopp video' : 'Vis video'}
                  </Button>
                  {videoInfo?.exists && (
                    <span data-testid="video-filename" className="text-xs text-[#64748B] truncate max-w-[220px]" title={videoInfo.filename}>
                      {videoInfo.filename}
                    </span>
                  )}
                </div>
                <p className="text-[11px] text-[#64748B] mt-2">Spilles i loop med lyd, dekker hele overlayet. Egner seg for lange klipp (f.eks. 30 min). Maks 2 GB.</p>
              </div>

              <div className="flex items-center gap-2 flex-wrap mt-4 pt-3 border-t border-[#334155]">
                <Button
                  data-testid="load-test-data"
                  variant="outline"
                  size="sm"
                  onClick={loadTestData}
                  className="bg-transparent border-yellow-600 text-yellow-400 hover:bg-yellow-900/30"
                >
                  <Star className="w-4 h-4 mr-2" />
                  Test Modus
                </Button>
                <Button
                  data-testid="clear-all-overlays"
                  variant="outline"
                  size="sm"
                  onClick={clearAllOverlays}
                  className="bg-transparent border-red-600 text-red-400 hover:bg-red-900/30"
                >
                  <XCircle className="w-4 h-4 mr-2" />
                  Fjern Alt
                </Button>
                <Button
                  data-testid="copy-overlay-url"
                  variant="outline"
                  size="sm"
                  onClick={copyOverlayUrl}
                  disabled={!selectedGame}
                  className="bg-transparent border-[#334155] text-white hover:bg-[#1E293B]"
                >
                  <Copy className="w-4 h-4 mr-2" />
                  Kopier URL
                </Button>
                <Button
                  data-testid="open-overlay"
                  size="sm"
                  onClick={openOverlay}
                  disabled={!selectedGame}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  <Tv className="w-4 h-4 mr-2" />
                  Åpne Overlay
                </Button>
                <Button
                  data-testid="open-vmix-overlay"
                  size="sm"
                  onClick={openVmixOverlay}
                  disabled={!selectedGame}
                  className="bg-purple-600 hover:bg-purple-700"
                >
                  <Monitor className="w-4 h-4 mr-2" />
                  vMix Overlay
                </Button>
              </div>
            </div>

          </div>
        </div>

        {/* Odds Table */}
        <OddsTable
          raceData={raceData}
          loading={loading}
          selectedHorseForSuper={selectedHorseForSuper}
          onHorseSelect={selectHorseForSuper}
        />

        {/* Footer Stats */}
        {gameData && (
          <div className="p-4 border-t border-[#334155] bg-[#0F172A] flex items-center gap-6">
            <div>
              <span className="text-xs text-[#94A3B8] uppercase">Turnover</span>
              <p className="font-mono text-lg text-white">
                {(gameData.turnover / 100).toLocaleString()} kr
              </p>
            </div>
            <div>
              <span className="text-xs text-[#94A3B8] uppercase block mb-1">Status</span>
              <span className={`px-2 py-1 rounded text-xs ${gameData.status === 'bettable' ? 'bg-green-500/10 text-green-500' : 'bg-gray-500/10 text-gray-500'}`}>
                {gameData.status}
              </span>
            </div>
            <div className="ml-auto text-right">
              <span className="text-xs text-[#94A3B8] uppercase">Sist oppdatert</span>
              <p className="font-mono text-sm text-white">
                {new Date(gameData.last_updated).toLocaleTimeString()}
              </p>
            </div>
          </div>
        )}

      </div>
      </div>{/* End Left 75% */}

      {/* Right 25%: Tabbed PTZ / Video Control */}
      <div style={{ width: '25%', height: '100vh', borderLeft: '2px solid #334155', display: 'flex', flexDirection: 'column', position: 'sticky', top: 0 }}>
        {/* Tab buttons */}
        <div style={{ display: 'flex', backgroundColor: '#0F172A', borderBottom: '2px solid #334155', flexShrink: 0 }}>
          <button
            data-testid="right-tab-ptz"
            onClick={() => setRightTab('ptz')}
            style={{
              flex: 1, padding: '10px 0', fontFamily: "'Barlow Condensed', sans-serif", fontSize: '0.85rem',
              fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', cursor: 'pointer',
              border: 'none', borderBottom: rightTab === 'ptz' ? '3px solid #3B82F6' : '3px solid transparent',
              backgroundColor: rightTab === 'ptz' ? '#1E293B' : 'transparent',
              color: rightTab === 'ptz' ? '#E2E8F0' : '#64748B',
              transition: 'all 0.15s',
            }}
          >
            PTZ Kamera
          </button>
          <button
            data-testid="right-tab-track"
            onClick={() => setRightTab('track')}
            style={{
              flex: 1, padding: '10px 0', fontFamily: "'Barlow Condensed', sans-serif", fontSize: '0.85rem',
              fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', cursor: 'pointer',
              border: 'none', borderBottom: rightTab === 'track' ? '3px solid #3B82F6' : '3px solid transparent',
              backgroundColor: rightTab === 'track' ? '#1E293B' : 'transparent',
              color: rightTab === 'track' ? '#E2E8F0' : '#64748B',
              transition: 'all 0.15s',
            }}
          >
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', justifyContent: 'center' }}>
              {autoTrackActive && (
                <span
                  data-testid="autotrack-tab-indicator"
                  style={{
                    width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#22C55E',
                    animation: 'autotrack-pulse 1.2s ease-in-out infinite',
                    boxShadow: '0 0 6px rgba(34,197,94,0.8)',
                  }}
                />
              )}
              Autotrack
            </span>
          </button>
          <button
            data-testid="right-tab-video"
            onClick={() => setRightTab('video')}
            style={{
              flex: 1, padding: '10px 0', fontFamily: "'Barlow Condensed', sans-serif", fontSize: '0.85rem',
              fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', cursor: 'pointer',
              border: 'none', borderBottom: rightTab === 'video' ? '3px solid #3B82F6' : '3px solid transparent',
              backgroundColor: rightTab === 'video' ? '#1E293B' : 'transparent',
              color: rightTab === 'video' ? '#E2E8F0' : '#64748B',
              transition: 'all 0.15s',
            }}
          >
            Video Kontroll
          </button>
        </div>

        {/* Tab content – both always mounted, hidden with display:none to preserve state/connections */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'auto' }}>
          <div style={{ backgroundColor: '#0F172A', display: rightTab === 'ptz' ? 'block' : 'none' }}>
            <PTZControl />
          </div>
          <div style={{ backgroundColor: '#0F172A', display: rightTab === 'track' ? 'block' : 'none' }}>
            <AutoTracker onTrackingChange={setAutoTrackActive} />
          </div>
          <div style={{ flex: 1, flexDirection: 'column', minHeight: 0, display: rightTab === 'video' ? 'flex' : 'none' }}>
              <div style={{ padding: '8px 12px', borderBottom: '1px solid #334155', backgroundColor: '#0F172A', display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                <input
                  data-testid="video-control-url-input"
                  type="text"
                  value={videoControlUrl}
                  onChange={(e) => { setVideoControlUrl(e.target.value); localStorage.setItem('video-control-url', e.target.value); }}
                  onKeyDown={(e) => { if (e.key === 'Enter') { setVideoControlUrlActive(normalizeVideoControlUrl(videoControlUrl)); } }}
                  placeholder="Lim inn URL til videokontroll..."
                  style={{ flex: 1, background: '#1E293B', border: '1px solid #334155', color: '#E2E8F0', fontSize: '0.75rem', padding: '5px 10px', borderRadius: '4px', fontFamily: "'JetBrains Mono', monospace", outline: 'none', minWidth: 0 }}
                />
                <button
                  data-testid="video-control-url-go"
                  onClick={() => setVideoControlUrlActive(normalizeVideoControlUrl(videoControlUrl))}
                  style={{ background: '#5CE0D6', border: 'none', color: '#050F23', fontSize: '0.7rem', fontWeight: 700, padding: '5px 12px', borderRadius: '4px', cursor: 'pointer', whiteSpace: 'nowrap' }}
                >
                  Last
                </button>
                {videoControlUrlActive && (
                  <a href={videoControlUrlActive} target="_blank" rel="noopener noreferrer" style={{ color: '#64748B', fontSize: '0.75rem', whiteSpace: 'nowrap' }} title="Åpne i nytt vindu">↗</a>
                )}
              </div>
              {videoControlUrlActive && videoControlUrlActive.startsWith('http://') && window.location.protocol === 'https:' && (
                <div data-testid="video-control-mixed-content-warning" style={{ padding: '6px 12px', backgroundColor: '#78350F', color: '#FDE68A', fontSize: '0.7rem', lineHeight: 1.4, flexShrink: 0 }}>
                  ⚠ Nettleseren kan blokkere http://-sider inne i en https-app. Hvis vinduet under er tomt: klikk skjold-/innstillingsikonet i adressefeltet og velg «Tillat usikkert innhold», eller åpne siden i eget vindu med ↗.
                </div>
              )}
              {videoControlUrlActive ? (
                <iframe
                  src={videoControlUrlActive}
                  title="Video Kontroll"
                  data-testid="video-control-iframe"
                  style={{ flex: 1, width: '100%', border: 'none', backgroundColor: '#050505' }}
                  allow="camera;microphone;fullscreen"
                />
              ) : (
                <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#475569', fontSize: '0.85rem', fontFamily: "'Barlow Condensed', sans-serif" }}>
                  Skriv inn URL og trykk Last
                </div>
              )}
            </div>
        </div>
      </div>

      {/* Bong Modal */}
      {showBongModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
          <div className="bg-[#0F172A] border border-[#334155] rounded-xl p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-barlow text-2xl font-bold text-white uppercase">
                Lag Bong - {gameTypeLabel(selectedGame?.game_type)}
              </h2>
              <button 
                onClick={() => setShowBongModal(false)}
                className="text-[#94A3B8] hover:text-white"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Tabs */}
            <div className="flex gap-2 mb-4">
              <button
                data-testid="bong-tab-tips"
                onClick={() => setBongModalTab('tips')}
                className={`flex-1 py-2 text-sm font-bold rounded-lg border-2 transition-all ${
                  bongModalTab === 'tips'
                    ? 'bg-green-600/20 border-green-500 text-green-400'
                    : 'bg-transparent border-[#334155] text-[#94A3B8] hover:text-white'
                }`}
              >
                Tipsekspert
              </button>
              <button
                data-testid="bong-tab-guest"
                onClick={() => setBongModalTab('guest')}
                className={`flex-1 py-2 text-sm font-bold rounded-lg border-2 transition-all ${
                  bongModalTab === 'guest'
                    ? 'bg-blue-600/20 border-blue-500 text-blue-400'
                    : 'bg-transparent border-[#334155] text-[#94A3B8] hover:text-white'
                }`}
              >
                Gjest
              </button>
            </div>

            {/* Name input */}
            <div className="mb-4">
              <label className="text-xs text-[#94A3B8] mb-1 block">Visningsnavn</label>
              <Input
                data-testid={`bong-name-${bongModalTab}`}
                type="text"
                placeholder={bongModalTab === 'tips' ? 'Tipsekspert' : 'Gjest'}
                value={bongModalTab === 'tips' ? bongNameTips : bongNameGuest}
                onChange={(e) => bongModalTab === 'tips' ? setBongNameTips(e.target.value) : setBongNameGuest(e.target.value)}
                className="bg-[#1E293B] border-[#334155] text-white"
              />
            </div>

            <p className="text-sm text-[#94A3B8] mb-4">
              Skriv inn hestenummer separert med komma. Bruk / for å dele i liten/stor bong
            </p>

            <div className="space-y-3">
              {Array.from({ length: getLegsCount() }, (_, i) => i + 1).map((leg) => (
                <div key={leg} className="flex items-center gap-3">
                  <span className="font-mono text-white w-16">
                    {legLabel(selectedGame?.game_type, leg)}
                  </span>
                  <Input
                    data-testid={`bong-input-${bongModalTab}-${leg}`}
                    type="text"
                    placeholder="1,2,3"
                    value={bongModalTab === 'tips' ? (bongData[leg] || '') : (bongDataGuest[leg] || '')}
                    onChange={(e) => {
                      if (bongModalTab === 'tips') {
                        setBongData({ ...bongData, [leg]: e.target.value });
                      } else {
                        setBongDataGuest({ ...bongDataGuest, [leg]: e.target.value });
                      }
                    }}
                    className="bg-[#1E293B] border-[#334155] text-white"
                  />
                </div>
              ))}
            </div>

            {/* Cost Calculator */}
            <div className="mt-6 p-4 bg-[#1E293B] rounded-lg border border-[#334155]">
              {bongModalTab === 'tips' ? (
                hasSplitBong() ? (
                  <>
                    <div className="flex items-center justify-between">
                      <span className="text-[#94A3B8]">Antall rekker:</span>
                      <span className="font-mono text-white text-lg">{calculateRows().small} / {calculateRows().large}</span>
                    </div>
                    <div className="flex items-center justify-between mt-2">
                      <span className="text-[#94A3B8]">Kostnad:</span>
                      <span className="font-mono text-green-400 text-xl font-bold">{calculateBongCost().small.toFixed(2)} kr / {calculateBongCost().large.toFixed(2)} kr</span>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="flex items-center justify-between">
                      <span className="text-[#94A3B8]">Antall rekker:</span>
                      <span className="font-mono text-white text-lg">{calculateRows().small}</span>
                    </div>
                    <div className="flex items-center justify-between mt-2">
                      <span className="text-[#94A3B8]">Kostnad:</span>
                      <span className="font-mono text-green-400 text-xl font-bold">{calculateBongCost().small.toFixed(2)} kr</span>
                    </div>
                  </>
                )
              ) : (
                (() => {
                  let gRows = 1;
                  const legs = getLegsCount();
                  for (let i = 1; i <= legs; i++) {
                    const v = bongDataGuest[i] || '';
                    const horses = v.includes('/') ? v.split('/')[0].split(',').filter(h => h.trim()).length : v.split(',').filter(h => h.trim()).length;
                    if (horses > 0) gRows *= horses;
                  }
                  const gPricing = getGamePricing();
                  const gRaw = gRows * gPricing.pricePerRow;
                  const gAvgift = gPricing.hasBongavgift ? Math.min(Math.ceil(gRaw / 50), 5) : 0;
                  const gCost = gRaw + gAvgift;
                  return (
                    <>
                      <div className="flex items-center justify-between">
                        <span className="text-[#94A3B8]">Antall rekker:</span>
                        <span className="font-mono text-white text-lg">{gRows}</span>
                      </div>
                      <div className="flex items-center justify-between mt-2">
                        <span className="text-[#94A3B8]">Kostnad:</span>
                        <span className="font-mono text-blue-400 text-xl font-bold">{gCost.toFixed(2)} kr</span>
                      </div>
                    </>
                  );
                })()
              )}
            </div>

            <div className="flex gap-3 mt-6">
              <Button
                variant="outline"
                onClick={() => setShowBongModal(false)}
                className="flex-1 bg-transparent border-[#334155] text-white hover:bg-[#1E293B]"
              >
                Avbryt
              </Button>
              <Button
                data-testid={`save-bong-${bongModalTab}`}
                onClick={bongModalTab === 'tips' ? saveBong : saveGuestBong}
                className={`flex-1 ${bongModalTab === 'tips' ? 'bg-green-600 hover:bg-green-700' : 'bg-blue-600 hover:bg-blue-700'}`}
              >
                Lagre {bongModalTab === 'tips' ? 'Bong' : 'Gjestebong'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Navnsuper Modal */}
      {showNavnsuperModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
          <div className="bg-[#0F172A] border border-[#334155] rounded-xl p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-barlow text-2xl font-bold text-white uppercase">
                Lag Navnsuper
              </h2>
              <button 
                onClick={() => setShowNavnsuperModal(false)}
                className="text-[#94A3B8] hover:text-white"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <p className="text-sm text-[#94A3B8] mb-4">
              Legg til navn og tittel som vises på overlay
            </p>

            <div className="space-y-4">
              <div>
                <label className="text-xs text-[#94A3B8] uppercase tracking-wider mb-2 block">
                  Navn (påkrevd)
                </label>
                <Input
                  data-testid="navnsuper-name-input"
                  value={navnsuperName}
                  onChange={(e) => setNavnsuperName(e.target.value)}
                  placeholder="F.eks. Ola Nordmann"
                  className="bg-[#1E293B] border-[#334155] text-white placeholder:text-[#64748B]"
                />
              </div>
              <div>
                <label className="text-xs text-[#94A3B8] uppercase tracking-wider mb-2 block">
                  Tittel (valgfritt)
                </label>
                <Input
                  data-testid="navnsuper-title-input"
                  value={navnsuperTitle}
                  onChange={(e) => setNavnsuperTitle(e.target.value)}
                  placeholder="F.eks. Travekspert"
                  className="bg-[#1E293B] border-[#334155] text-white placeholder:text-[#64748B]"
                />
              </div>
            </div>

            {/* List of existing navnsupers */}
            {navnsuperList.length > 0 && (
              <div className="mt-6">
                <label className="text-xs text-[#94A3B8] uppercase tracking-wider mb-2 block">
                  Lagrede Navnsuper
                </label>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {navnsuperList.map((ns) => (
                    <div 
                      key={ns.id}
                      className="flex items-center justify-between bg-[#1E293B] p-2 rounded"
                    >
                      <div>
                        <span className="text-white font-medium">{ns.name}</span>
                        {ns.title && <span className="text-[#94A3B8] ml-2">- {ns.title}</span>}
                      </div>
                      <button 
                        onClick={() => deleteNavnsuper(ns.id)}
                        className="text-red-400 hover:text-red-300"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex gap-3 mt-6">
              <Button
                variant="outline"
                onClick={() => setShowNavnsuperModal(false)}
                className="flex-1 bg-transparent border-[#334155] text-white hover:bg-[#1E293B]"
              >
                Avbryt
              </Button>
              <Button
                onClick={saveNavnsuper}
                className="flex-1 bg-purple-600 hover:bg-purple-700"
              >
                Lagre Navnsuper
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Crawl Text Modal */}
      {showCrawlModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
          <div className="bg-[#0F172A] border border-[#334155] rounded-lg p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-barlow text-xl font-bold uppercase text-white">
                Rediger Crawl-tekst
              </h3>
              <button 
                onClick={() => setShowCrawlModal(false)}
                className="text-[#94A3B8] hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Source selector */}
            <div className="flex gap-2 mb-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCrawlSource('manual')}
                className={`flex-1 ${crawlSource === 'manual' ? 'bg-cyan-600 border-cyan-600' : 'bg-transparent border-[#334155]'} text-white hover:bg-cyan-700`}
              >
                Manuell tekst
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCrawlSource('rss')}
                className={`flex-1 ${crawlSource === 'rss' ? 'bg-cyan-600 border-cyan-600' : 'bg-transparent border-[#334155]'} text-white hover:bg-cyan-700`}
              >
                RSS Feed
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setCrawlSource('top3');
                  // Auto-generate top 3 text
                  if (gameData?.races?.length) {
                    const parts = gameData.races.map((race) => {
                      const sorted = [...race.horses]
                        .filter(h => h.distribution > 0)
                        .sort((a, b) => b.distribution - a.distribution)
                        .slice(0, 3);
                      if (sorted.length === 0) return null;
                      const horsesStr = sorted
                        .map((h) => `${h.number}. ${h.name} (${h.distribution.toFixed(1)}%)`)
                        .join('  \u2022  ');
                      return `${selectedGame?.game_type || 'V65'}-${race.leg_number}: ${horsesStr}`;
                    }).filter(Boolean);
                    setCrawlText(parts.join('    |    '));
                  }
                }}
                className={`flex-1 ${crawlSource === 'top3' ? 'bg-amber-600 border-amber-600' : 'bg-transparent border-[#334155]'} text-white hover:bg-amber-700`}
              >
                <Trophy className="w-3.5 h-3.5 mr-1" />
                Topp 3
              </Button>
            </div>

            <div className="space-y-4">
              {crawlSource === 'manual' ? (
                <div>
                  <label className="text-xs text-[#94A3B8] uppercase tracking-wider mb-2 block">
                    Tekst som skal rulle
                  </label>
                  <textarea
                    data-testid="crawl-text-input"
                    value={crawlText}
                    onChange={(e) => setCrawlText(e.target.value)}
                    placeholder="Skriv inn tekst som skal vises nederst på skjermen..."
                    rows={3}
                    className="w-full bg-[#1E293B] border border-[#334155] text-white placeholder:text-[#64748B] rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-cyan-500"
                  />
                </div>
              ) : crawlSource === 'top3' ? (
                <div className="space-y-3">
                  {!gameData?.races?.length ? (
                    <p className="text-sm text-red-400">Ingen spilldata tilgjengelig. Velg et spill først.</p>
                  ) : (
                    <div className="bg-[#1E293B] border border-[#334155] rounded-md p-3 max-h-48 overflow-y-auto">
                      {gameData.races.map((race) => {
                        const top = [...race.horses]
                          .filter(h => h.distribution > 0)
                          .sort((a, b) => b.distribution - a.distribution)
                          .slice(0, 3);
                        return (
                          <div key={race.leg_number} className="py-1.5 border-b border-[#334155] last:border-0">
                            <span className="text-amber-400 font-bold text-sm">{selectedGame?.game_type || 'V65'}-{race.leg_number}: </span>
                            {top.map((h, i) => (
                              <span key={h.number} className="text-white text-sm">
                                {i > 0 && <span className="text-[#64748B]"> {'\u2022'} </span>}
                                {i + 1}. {h.name} <span className="text-[#94A3B8]">({h.distribution.toFixed(1)}%)</span>
                              </span>
                            ))}
                            {top.length === 0 && <span className="text-[#64748B] text-sm">Ingen data</span>}
                          </div>
                        );
                      })}
                    </div>
                  )}
                  <div>
                    <label className="text-xs text-[#94A3B8] uppercase tracking-wider mb-1 block">Forhåndsvisning (kan redigeres)</label>
                    <textarea
                      value={crawlText}
                      onChange={(e) => setCrawlText(e.target.value)}
                      rows={2}
                      className="w-full bg-[#1E293B] border border-[#334155] text-white rounded-md px-3 py-2 text-xs focus:outline-none focus:ring-1 focus:ring-amber-500"
                    />
                  </div>
                  <Button
                    onClick={() => {
                      if (gameData?.races?.length) {
                        const parts = gameData.races.map((race) => {
                          const sorted = [...race.horses]
                            .filter(h => h.distribution > 0)
                            .sort((a, b) => b.distribution - a.distribution)
                            .slice(0, 3);
                          if (sorted.length === 0) return null;
                          const horsesStr = sorted
                            .map((h) => `${h.number}. ${h.name} (${h.distribution.toFixed(1)}%)`)
                            .join('  \u2022  ');
                          return `${selectedGame?.game_type || 'V65'}-${race.leg_number}: ${horsesStr}`;
                        }).filter(Boolean);
                        setCrawlText(parts.join('    |    '));
                      }
                    }}
                    size="sm"
                    disabled={!gameData?.races?.length}
                    className="bg-amber-600 hover:bg-amber-700 text-xs"
                  >
                    <RefreshCw className="w-3 h-3 mr-1" />
                    Oppdater med ferske data
                  </Button>
                </div>
              ) : (
                <>
                  <div>
                    <label className="text-xs text-[#94A3B8] uppercase tracking-wider mb-2 block">
                      RSS Feed URL
                    </label>
                    <div className="flex gap-2">
                      <input
                        data-testid="rss-url-input"
                        type="url"
                        value={rssUrl}
                        onChange={(e) => setRssUrl(e.target.value)}
                        placeholder="https://www.travsport.no/feed/"
                        className="flex-1 bg-[#1E293B] border border-[#334155] text-white placeholder:text-[#64748B] rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-cyan-500"
                      />
                      <Button
                        onClick={fetchRssFeed}
                        disabled={isLoadingRss}
                        className="bg-cyan-600 hover:bg-cyan-700"
                      >
                        {isLoadingRss ? 'Henter...' : 'Hent'}
                      </Button>
                    </div>
                  </div>

                  {rssHeadlines.length > 0 && (
                    <div>
                      <label className="text-xs text-[#94A3B8] uppercase tracking-wider mb-2 block">
                        Overskrifter ({rssHeadlines.length} stk)
                      </label>
                      <div className="bg-[#1E293B] border border-[#334155] rounded-md p-3 max-h-40 overflow-y-auto">
                        {rssHeadlines.map((headline, idx) => (
                          <div key={idx} className="text-sm text-white py-1 border-b border-[#334155] last:border-0">
                            {headline}
                          </div>
                        ))}
                      </div>
                      <p className="text-xs text-[#64748B] mt-2">
                        Disse overskriftene vil bli vist som rullende tekst, separert med •
                      </p>
                    </div>
                  )}
                </>
              )}
              <p className="text-xs text-[#64748B]">
                Teksten vil rulle fra høyre til venstre nederst på overlay-skjermen (40px høy mørkeblå bakgrunn med 30px hvit tekst).
              </p>
            </div>

            <div className="flex gap-3 mt-6">
              <Button
                variant="outline"
                onClick={() => setShowCrawlModal(false)}
                className="flex-1 bg-transparent border-[#334155] text-white hover:bg-[#1E293B]"
              >
                Avbryt
              </Button>
              <Button
                onClick={updateCrawlText}
                disabled={crawlSource === 'rss' && rssHeadlines.length === 0}
                className="flex-1 bg-cyan-600 hover:bg-cyan-700"
              >
                Lagre Tekst
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Message Popup Modal */}
      {showSponsorModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
          <div className="bg-[#0F172A] border border-[#334155] rounded-lg p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-white font-semibold text-lg">Sponsor-rull</h3>
              <button onClick={() => setShowSponsorModal(false)} className="text-[#94A3B8] hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-[#94A3B8] text-xs mb-3">Last opp sponsorlogoer (PNG/JPG, maks 3 MB). Logoene roterer hvert 10. sekund nederst i studio-rammens høyre kolonne.</p>
            <input
              data-testid="sponsor-file-input"
              type="file"
              accept="image/*"
              multiple
              disabled={sponsorUploading}
              onChange={(e) => { if (e.target.files?.length) { uploadSponsorFiles(Array.from(e.target.files)); e.target.value = ''; } }}
              className="w-full text-sm text-[#94A3B8] mb-4 file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:bg-[#334155] file:text-white file:cursor-pointer hover:file:bg-[#475569]"
            />
            {sponsorUploading && <p className="text-amber-400 text-xs mb-3">Laster opp...</p>}
            <div className="max-h-64 overflow-y-auto space-y-2 mb-4">
              {sponsorList.length === 0 && (
                <p className="text-[#64748B] text-sm text-center py-4">Ingen logoer lastet opp ennå</p>
              )}
              {sponsorList.map((s) => (
                <div key={s.id} className="flex items-center gap-3 bg-[#1E293B] border border-[#334155] rounded-md p-2" data-testid={`sponsor-item-${s.id}`}>
                  <div className="bg-white rounded w-20 h-10 flex items-center justify-center overflow-hidden flex-shrink-0">
                    <img src={`${API}/sponsors/${s.id}/image`} alt={s.name} className="max-w-full max-h-full object-contain" />
                  </div>
                  <span className="text-white text-xs flex-1 truncate">{s.name}</span>
                  <button
                    data-testid={`sponsor-delete-${s.id}`}
                    onClick={() => deleteSponsor(s.id)}
                    className="text-red-400 hover:text-red-300 flex-shrink-0"
                    title="Slett logo"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
            <Button
              data-testid="sponsor-toggle"
              onClick={toggleSponsors}
              disabled={sponsorList.length === 0 && !sponsorsActive}
              className={`w-full ${sponsorsActive ? 'bg-red-600 hover:bg-red-700' : 'bg-emerald-600 hover:bg-emerald-700'} text-white`}
            >
              {sponsorsActive ? 'Skjul sponsor-rull' : 'Vis sponsor-rull'}
            </Button>
          </div>
        </div>
      )}

      {showMessageModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
          <div className="bg-[#0F172A] border border-[#334155] rounded-lg p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-white font-semibold text-lg">Beskjed</h3>
              <button onClick={() => setShowMessageModal(false)} className="text-[#94A3B8] hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <textarea
              data-testid="message-input"
              value={messageText}
              onChange={(e) => setMessageText(e.target.value)}
              placeholder="Skriv beskjed her... Én beskjed per linje – flere linjer roterer hvert 10. sekund."
              rows={4}
              className="w-full bg-[#1E293B] border border-[#334155] text-white placeholder:text-[#64748B] rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-purple-500 resize-none"
            />
            <p className="text-[#64748B] text-xs mt-2">Tips: Skriv flere beskjeder på hver sin linje, så roterer de automatisk (10 sek per beskjed).</p>
            <div className="flex gap-2 mt-4">
              <Button
                onClick={() => setShowMessageModal(false)}
                variant="outline"
                className="flex-1 border-[#334155] text-[#94A3B8] hover:bg-[#1E293B]"
              >
                Lukk
              </Button>
              <Button
                data-testid="message-save"
                onClick={() => {
                  setShowMessageModal(false);
                  toast.success('Beskjed lagret');
                }}
                disabled={!messageText.trim()}
                className="flex-1 bg-purple-600 hover:bg-purple-700"
              >
                Lagre
              </Button>
            </div>
          </div>
        </div>
      )}

      {showV75Modal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
          <div className="bg-[#0F172A] border border-[#334155] rounded-lg p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-yellow-400 font-semibold text-lg">V75 Melding</h3>
              <button onClick={() => setShowV75Modal(false)} className="text-[#94A3B8] hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <textarea
              data-testid="v75-input"
              value={v75Text}
              onChange={(e) => setV75Text(e.target.value)}
              placeholder="Skriv V75-melding her..."
              rows={4}
              className="w-full bg-[#1E293B] border border-[#334155] text-white placeholder:text-[#64748B] rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-yellow-500 resize-none"
            />
            <div className="flex gap-2 mt-4">
              <Button
                onClick={() => setShowV75Modal(false)}
                variant="outline"
                className="flex-1 border-[#334155] text-[#94A3B8] hover:bg-[#1E293B]"
              >
                Lukk
              </Button>
              <Button
                data-testid="v75-save"
                onClick={() => {
                  setShowV75Modal(false);
                  toast.success('V75-melding lagret');
                }}
                disabled={!v75Text.trim()}
                className="flex-1 bg-yellow-600 hover:bg-yellow-700"
              >
                Lagre
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Results Bong Comparison Modal */}
      {showResultsModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-[#0F172A] border border-[#334155] rounded-lg p-6 w-[500px] max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-white font-semibold text-lg">Skriv inn Resultat-tips</h3>
              <Button variant="ghost" size="sm" onClick={() => setShowResultsModal(false)}>
                <X className="w-4 h-4 text-white" />
              </Button>
            </div>
            <p className="text-sm text-gray-400 mb-4">
              Skriv inn dine tips per avdeling (hestenummer separert med komma). Tipsene lagres automatisk.
            </p>
            {[1,2,3,4,5,6].map(leg => (
              <div key={leg} className="mb-3">
                <label className="text-sm text-gray-300 font-semibold">
                  {selectedGame?.game_type || 'V65'}-{leg}:
                </label>
                <input
                  type="text"
                  value={resultsBong[leg] || ''}
                  onChange={(e) => setResultsBong(prev => {
                    const updated = { ...prev, [leg]: e.target.value };
                    localStorage.setItem('atg-results-bong', JSON.stringify(updated));
                    return updated;
                  })}
                  placeholder="f.eks. 1,3,5"
                  className="w-full mt-1 bg-[#1E293B] border border-[#334155] rounded px-3 py-2 text-white text-sm"
                />
              </div>
            ))}
            <div className="flex gap-2 mt-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowResultsModal(false)}
                className="flex-1 border-[#334155] text-white"
              >
                Lukk
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
